/**
 * Project store — projects are the top-level container. Each project locks
 * to one (device, orientation) pair and owns its own pages[]. Each page
 * holds its own items[]; nextCC stays at project level so CCs flow across
 * pages as the user adds controls. activePageId selects which page the
 * canvas + edit-mode actions act on.
 *
 * Persistence is one localStorage key per major shape:
 *   v1 (legacy):  Project { items, nextCC }                   — pre-pages
 *   v2 (current): Project { pages: Page[], activePageId, nextCC } — multi-page
 *
 * On load we try v2; if absent, we migrate v1 → v2 and leave v1 in place
 * for one release as a safety net. The active project id is stored
 * separately so reloads return the user to their last project without
 * deserializing the whole projects map.
 */

import { idSuffix } from '../utils/idSuffix.ts';
import { createSignal, createMemo, type Accessor } from 'solid-js';
import type { DeviceId, Orientation } from './devices.ts';
import { canvasSize, canvasSizeOf, LEGACY_IPHONE_FIX_FACTOR } from './devices.ts';
import { sizeFor } from './sizes.ts';
import type { LfoWaveform, AbletonStep, RecQuantLabel, RecQuantVia, DmxTarget } from '@opbuttons/schema';
import type { AbletonRowControl } from '../ableton/rows.ts';
import { createHistory } from '../undo/history.ts';
import type { OpVisualAction, OpvQuant, OpvVia } from '../opvisual/wire.ts';
import type { HueSceneRef } from '../lighting/hueScenes.ts';
import type { SynAction, SynWhen } from '../synesthesia/model.ts';
import { recordDeletion } from './tombstones.ts';
import { noteProjectOpened } from '../navigate/recentProjects.ts';
import { dropClaimsOfItems } from '../matrix/store.ts';
import { pushToast } from '../ui/toast.ts';
import { canonicalJson } from '../timeline/librarySync.ts';
import { projectReadOnly } from './readOnly.ts';
import type { GridFinish, GridTitleAt, GridIconAt, GridTexture, GridTexSize, GridStarts, GridBeatFlash } from './buttonLook.ts';
import type { InstrumentIconId } from '../components/InstrumentIcon.tsx';
import type { DisplayLabelAt, DisplayAlign, DisplaySongAlign } from './displayLook.ts';

const PROJECTS_KEY_V1 = 'opbuttons.projects.v1';
const PROJECTS_KEY_V2 = 'opbuttons.projects.v2';
const PROJECTS_KEY_V3 = 'opbuttons.projects.v3';
const PROJECTS_KEY_V4 = 'opbuttons.projects.v4';
const PROJECTS_KEY_V5 = 'opbuttons.projects.v5';
const ACTIVE_KEY      = 'opbuttons.activeProject';

/** MIDI port identifier. Numeric so port ids are permanent (user-facing
 *  labels in Settings can change, ids cannot). Currently 1 or 2; expandable
 *  later without breaking older projects. */
export type PortId = 1 | 2;

/** Default destinations for any control sending MIDI: Port 1 only.
 *  Multi-port broadcast is opt-in via the InspectorPanel. */
export const DEFAULT_DESTINATIONS: PortId[] = [1];

export type LayoutColor = 'cyan' | 'magenta' | 'amber' | 'green';

/** Size preset — drives the spawn dimensions and also serves as the
 *  freeform-drag clamps (S = min, L = max, M = mid). Per-kind pixel
 *  values live in `SIZE_PRESETS` (layout/sizes.ts). */
export type SizePreset = 'S' | 'M' | 'L';

/** Face-display options shared by every item kind that has a visible name
 *  and/or info line. `undefined` = default (visible). The InspectorPanel
 *  surfaces toggles for each. */
/** Per-item name-font sizing. `auto` (default) runs the per-line
 *  auto-shrink helper — each line's font shrinks until it fits within
 *  the face's usable width. Explicit S/M/L/XL locks a fixed rem-multiple
 *  and skips the auto-shrink; the operator picks this when they want
 *  a consistent visual weight across cells regardless of label length. */
export type NameSize = 'auto' | 'S' | 'M' | 'L' | 'XL';

/** Per-item colour (DEC-127). RGB plus the three in-betweens plus white,
 *  plus an explicit no-colour. Six-point wheel, which is also the
 *  stage-lighting convention, so it matches how the operator already thinks
 *  about colour - and eight chips is a tap-target-friendly row on an iPad
 *  where a colour wheel is not.
 *
 *  ORTHOGONAL TO THE SKIN and orthogonal to STATE. It washes the item's
 *  surfaces subtly and its lit visuals strongly; the rest -> armed -> live
 *  ramp still belongs to the skin. That separation is the whole point: a
 *  colour tells you WHICH control this is, brightness tells you what it is
 *  DOING. This replaces the old four-value `LayoutColor`, which named
 *  STATE tokens (idle/incoming/armed/ok) and had degenerated under the skin
 *  system to two distinguishable colours out of four. */
export type PaletteHue =
  | 'none' | 'red' | 'yellow' | 'green' | 'cyan' | 'blue' | 'magenta' | 'white'
  // DEC-316 (2026-10-26, operator: "i want 16 distinct colours"): nine more,
  // placed in OKLCH by a search for the largest smallest difference to each
  // other and to the seven above, lit and at rest (closest lit pair
  // amber/yellow, 0.115 OKLab). The seven keep their exact values.
  | 'orange' | 'amber' | 'lime' | 'mint' | 'teal' | 'sky' | 'violet' | 'purple' | 'pink';

/** Every swatch is LUMINANCE-LIFTED and that is a requirement, not taste.
 *  State has to read by brightness because stage wash kills hue; a dark hue
 *  at 68% over the live background drags live down toward rest and flattens
 *  the ramp. Pure #0000ff is L*32 and pure #ff0000 is L*53 - both fail that,
 *  blue badly. These sit in an L*72-100 band. Red and blue are deliberately
 *  NOT pure: "correcting" them reintroduces the exact defect this prevents. */
export const HUE_VALUES: Record<PaletteHue, string> = {
  none:    'transparent',
  red:     '#ff4040',
  yellow:  '#ffd400',
  green:   '#00ff6a',
  cyan:    '#00d5ff',
  blue:    '#3d6eff',
  magenta: '#ff3dd4',
  white:   '#ffffff',
  // DEC-316's nine, from button-look-study.html (measured there, not by eye).
  orange:  '#e37705',
  amber:   '#f1a70a',
  lime:    '#daff08',
  mint:    '#10efc7',
  teal:    '#02a6ac',
  sky:     '#52a9fe',
  violet:  '#9353fe',
  purple:  '#ca8cfe',
  pink:    '#ff879f',
};

/** Order follows the wheel (R -> Y -> G -> C -> B -> M) so the swatch strip
 *  reads as a spectrum rather than as an arbitrary list. */
export const HUE_ORDER: PaletteHue[] = [
  'none', 'red', 'orange', 'amber', 'yellow', 'lime', 'green', 'mint', 'teal',
  'cyan', 'sky', 'blue', 'violet', 'purple', 'magenta', 'pink', 'white',
];

/** Read-time migration from the retired `LayoutColor`. No data is rewritten:
 *  an item that never had a hue picked keeps rendering from its legacy
 *  colour until someone picks one. Only `amber` needs a rename; the other
 *  three names already exist in the new palette. */
export function hueOf(item: { hue?: PaletteHue; color?: string }): PaletteHue {
  if (item.hue) return item.hue;
  switch (item.color) {
    case 'amber':   return 'yellow';
    case 'cyan':    return 'cyan';
    case 'magenta': return 'magenta';
    case 'green':   return 'green';
    default:        return 'none';
  }
}

/** True when an item is not pointed at anything yet, so pressing it can only
 *  do nothing. Operator 2026-07-30: *"we can use a soft red pulse on an item if
 *  it's not assigned."*
 *
 *  ONE predicate, not a per-component check, for two reasons. It is the hook
 *  `EditableItem` sets `data-unassigned` from, so the pulse is a single CSS rule
 *  that covers every kind including kinds added later. And the performance-mode
 *  pre-flight linter reads the SAME function, so the two surfaces can never
 *  disagree about what "assigned" means - which matters because the pulse is
 *  deliberately suppressed in performance mode and the linter is what replaces it
 *  there.
 *
 *  Only kinds that genuinely point at an EXTERNAL thing can be unassigned. A
 *  button with no name is still a working button; a Stream Deck key with no
 *  action is a dead control that looks alive, which is the failure this whole
 *  component family was rebuilt to prevent (see StreamDeckButton's header). */
export function isUnassigned(item: LayoutItem): boolean {
  switch (item.kind) {
    // The action is an operator-chosen Elgato UUID; absent = points nowhere.
    case 'streamdeck': return !(item as { actionId?: string }).actionId?.trim();
    // `timelineId` is typed required but is empty on a fresh spawn, and the
    // component already renders a "Pick a timeline in the Inspector" fallback
    // for exactly this case.
    // NOTE the camelCase: the discriminant is 'timelineLauncher', not
    // 'timelinelauncher'. Written wrong first, and TypeScript caught it only
    // because this is a discriminated union - the same guessed-string mistake
    // that shipped `.xy-pad` for `.xypad` and `data-state` for `data-on`, both
    // of which were silent. Read the interface, do not infer from siblings.
    case 'timelineLauncher': return !(item as { timelineId?: string }).timelineId?.trim();
    default: return false;
  }
}

export interface FaceOptions {
  /** Optional override for the auto-derived name shown on the face. */
  name?: string;
  /** Hide the name on the face (default visible). */
  hideName?: boolean;
  /** Hide the info line (cc/ch/key/etc) on the face (default visible). */
  hideLabel?: boolean;
  /** Name-font sizing strategy. Default `auto`. */
  nameSize?: NameSize;
  /** Per-item colour (DEC-127). Absent = no colour = byte-identical to
   *  before, because the mechanism is a no-op until someone picks one. */
  hue?: PaletteHue;
  /** Where the item's Name shows in line, on the kinds drawn as a tray (a button grid, and DEC-321's rows of choices:
   *  components/TrayTitle.tsx). Default top left; no Name, no title. */
  titleAt?: GridTitleAt;
  // DEC-317 on single pads (DEC-321, components/PadDecor.tsx): the grid's finish, icon and texture. All optional.
  finish?: GridFinish;
  icon?: InstrumentIconId | 'none';
  iconAt?: GridIconAt;
  texture?: GridTexture;
  texSize?: GridTexSize;
  texColour?: 'auto' | 'dark' | Exclude<PaletteHue, 'none'>;
  texStrength?: number;
}

/** Extended palette used by ButtonGrid cells. Each cell picks a color from
 *  this expanded set so a 10-cell row reads as distinct genres at a glance.
 *  Maps to the secondary tokens in design-tokens.css (violet/yellow/pink/
 *  sky/tan are intentionally separate from the bridge-status meaning of
 *  cyan/amber/magenta/green). */
export type CellColor = LayoutColor | 'violet' | 'yellow' | 'pink' | 'sky' | 'tan' | 'red';

/** Muted page-pill hue palette. Distinct enough to tell apart at a glance,
 *  calm enough not to compete with control colors. OKLCH values locked in
 *  the pages spec — sit at L≈65-72, C≈0.07-0.09 (vivid control palette is
 *  L≈82-90, C≈0.18-0.30, so the two never collide). */
export type PageHue = 'sky' | 'violet' | 'amber' | 'teal' | 'rose' | 'forest';

const PAGE_HUE_PALETTE: PageHue[] = ['sky', 'violet', 'amber', 'teal', 'rose', 'forest'];

/** Forward-compatible placeholder for the Universal Trigger System (see
 *  `memory/project_universal_trigger_vision.md`). Pages carry an optional
 *  `triggers?` field so layouts authored today survive the day the real
 *  Trigger union ships. Runtime ignores the field. */
export type Trigger = unknown;

/**
 * Action surface placeholder. Every control will eventually carry this
 * optional field so the editor's "Advanced…" modal can expose richer
 * behaviors (multi-MIDI fanout, OSC, macro arming, Stream Deck) without a
 * schema migration. v1 doesn't render the advanced UI — the field is here
 * so layouts authored today are forward-compatible.
 *
 * The shape is intentionally narrow: anything not listed below is added
 * in a later stage. When `primary` is present it OVERRIDES the basic
 * fields (ccNumber/onValue) for that control. When absent, the basic
 * fields drive behavior.
 */
export interface ControlActions {
  /** Replaces the basic MIDI send. If present, the basic ccNumber/onValue
   *  on the control are ignored. */
  primary?: ActionStep[];
  /** Fires on deactivate (e.g. tapping the active cell off). */
  release?: ActionStep[];
  /** Macro id to arm in addition to (or instead of) primary. */
  macroId?: string;
}

/** Discriminated union of action types. v1 supports just the MIDI step;
 *  additional kinds (osc, sd, macro, setControl) will be added with the
 *  Advanced editor. */
export type ActionStep =
  | { kind: 'midi'; midiKind: 'cc' | 'noteon' | 'noteoff'; channel: number; number: number; value: number };

/** A single cell in a ButtonGrid. Most fields fall back to the grid's
 *  defaults when omitted, so the common case (sequential CCs) needs no
 *  per-cell overrides at all. */
export interface ButtonGridCell {
  /** Stable id, ULID-shaped. */
  id: string;
  /** Display label on the cell face. Defaults to the cell's 1-indexed number. */
  label?: string;
  /** Override the grid's baseCC + index for this cell. Optional. */
  ccNumber?: number;
  /** Override the grid's channel for this cell. Optional. */
  channel?: number;
  /** Override the grid's onValue for this cell. Optional. */
  onValue?: number;
  /** Override the auto-assigned color for this cell. Optional. */
  color?: CellColor;
  /** Name-font sizing strategy. Default `auto`. See NameSize doc. */
  nameSize?: NameSize;
  /** Advanced action surface — same as the grid-level one. v1 hides this. */
  actions?: ControlActions;
}

/** Press/release semantics for a button.
 *  - `toggle` (default): each tap flips state. Sends ON then later OFF.
 *  - `momentary`: press = ON, release = OFF. Two MIDI events per tap.
 *    Pointer-up anywhere ends the press (releasing outside the bounds
 *    still sends OFF) so a finger that drags off the button doesn't
 *    leave a stuck-on note.
 *  - `trigger`: one-shot. Press fires ON then OFF in the same tick;
 *    release does nothing. Useful for scene-launch / scratch-fire feel.
 *  TouchOSC's `buttonType` 0/1 maps to momentary/toggle respectively. */
export type ButtonMode = 'toggle' | 'momentary' | 'trigger';

/** Visual shape on the face. 12-shape library grouped by family:
 *   - Rectangles: rect (rounded, current default), pill (capsule), bar
 *     (slim + wide, ≥ 2:1 aspect enforced)
 *   - Curves: circle (1:1 locked), ellipse (free aspect)
 *   - Polygons: triangle / pentagon / hexagon / octagon (all 1:1 locked,
 *     point up at rotation 0), diamond (rhombus, taller than wide)
 *   - Affordances: arrow / chevron (free aspect, point right at rotation 0
 *     — rotate to face any direction)
 *  Aspect rules live in shapes.ts (shapeAspectRule). Hit-area follows the
 *  shape: CSS-family (rect/pill/bar/circle/ellipse) via border-radius,
 *  polygon-family via clip-path — pointer events outside the clipped area
 *  don't fire. */
export type ButtonShape =
  | 'rect' | 'pill' | 'bar'
  | 'circle' | 'ellipse'
  | 'triangle' | 'diamond' | 'pentagon' | 'hexagon' | 'octagon'
  | 'arrow' | 'chevron';

/** What MIDI message the button fires. CC stays the default (preserves
 *  v2 behavior). NoteOn fires `noteon note=note velocity=velocity` on
 *  activate; in momentary mode the release fires a paired `noteoff`.
 *  PC fires a single two-byte Program Change and has no OFF — see the
 *  `program` field. */
export type ButtonActionType = 'cc' | 'noteon' | 'pc' | 'ableton' | 'obs' | 'abletonDevice'
  // Run a program registered on one machine's OPBridge (see `runProgramId`).
  // Named `runProgram`, not `program`, because `program` is already the
  // Program Change NUMBER field on this same item.
  | 'runProgram'
  // Play one of OPBridge's built-in lighting presets (see `lightPreset`).
  | 'lightPreset'
  // Recall a scene from the Hue app (see `hueScene`, 2026-10-23).
  | 'hueScene'
  // Tell Synesthesia (the VJ app) to change scene or look (see `synesthesia`, 2026-11-05).
  | 'synesthesia';

/** What a Synesthesia button sends (`actionType === 'synesthesia'`): the action, and when it lands. */
export type SynButtonConfig = SynAction & { when?: SynWhen };

/**
 * What a Light Preset button plays (`actionType === 'lightPreset'`): one of
 * OPBridge's built-in presets (`GET /lighting/presets`, DEC-601), with the
 * operator's knob choices and the lights to aim it at.
 *
 * Only OVERRIDES are stored in `knobs`: a knob the operator never touched
 * is left out, so it takes the preset's own default on that bridge - and a
 * default OPBridge later improves reaches this button with no edit.
 */
export interface LightPresetConfig {
  /** Preset id. Empty = unconfigured: shown on the face and refused. */
  presetId: string;
  /** Knob overrides by key: `"#rrggbb"` (colour), option label (choice),
   *  0..1 (level). */
  knobs?: Record<string, string | number>;
  /** Which lights. Absent or `all` = the whole rig in room order, which is
   *  what a `global` preset is designed for. Zones are not offered: the
   *  preset contract takes fixture ids. */
  target?: DmxTarget;
  /** Beats to run before stopping by itself. Absent = until stopped. */
  lengthBeats?: number;
}

/**
 * The action types that actually put MIDI bytes on a port.
 *
 * ⚠️ This exists because the opposite default has now bitten twice, in two
 * files, silently both times. `ccClaims` (`performance/lint.ts`) and
 * `resolveFireableItemActions` (`timeline/bridgeSync.ts`) both used to read
 * "assume `cc` unless this actionType is named here", so every non-MIDI
 * action type had to remember to opt OUT of two places it has no reason to
 * know about. `ableton` was the first to fall in (it linted a phantom `cc 0`
 * collision for a whole arc); `obs` would have been the second, and the
 * timeline half of it pushed REAL `[0xB0, 0, 127]` bytes at Ableton from a
 * step drawn as a scene change - a wrong send at a gig, not a no-op.
 *
 * So the default is inverted: a NEW action type emits no MIDI until it is
 * added to this set, which is the safe direction to be wrong in. Adding a
 * MIDI-emitting type and forgetting this set makes the button visibly inert;
 * forgetting it the old way made the button send the wrong bytes to real
 * hardware.
 *
 * `midiAlloc.ts::midiSlotsOf` already asked the question this way round
 * (`if (actionType === 'cc') … if (actionType === 'noteon') …`, returning
 * `[]` otherwise) and was never once wrong, which is the evidence this shape
 * is the correct one.
 */
const MIDI_EMITTING_ACTION_TYPES: ReadonlySet<ButtonActionType> = new Set<ButtonActionType>([
  'cc', 'noteon', 'pc',
]);

/**
 * Does this Button's action type put MIDI bytes on a port?
 *
 * Absent `actionType` means `cc` (the v2 default every legacy button
 * migrates as), so an undefined answer is `true` - matching what those
 * buttons have always done.
 */
export function buttonEmitsMidi(actionType: ButtonActionType | undefined): boolean {
  return MIDI_EMITTING_ACTION_TYPES.has(actionType ?? 'cc');
}

/**
 * Which Ableton device a Button toggles, as the OPERATOR authored it.
 *
 * **Addressed by NAME, never by position.** `OP-AbletonDeviceScan`'s own wire
 * contract warns that flat device indices "are positions and shift when the
 * chain changes", so an index stored in a saved layout silently retargets the
 * moment the operator inserts a device. The name-based `set_match` verb
 * exists precisely for this.
 *
 * `nth` disambiguates a repeated name (two Reverbs on one track) and is
 * 1-based to match how the operator counts them. **A name matching nothing,
 * or `nth` beyond the matches, is a VISIBLE dead state on the face** - not a
 * silent no-op. That is only possible because this action type is lit from
 * readback: name-based addressing and state readback are one feature, not
 * two, and the plan notepad records why.
 */
export interface AbletonDeviceTarget {
  /** Track name as shown in Live. Chosen over a track index deliberately:
   *  reordering tracks is commonplace and renaming one mid-set is not.
   *  `live_control.py` independently made the same choice, and REFUSES an
   *  ambiguous name rather than picking - which this must too. */
  trackName: string;
  /** Device name as shown in Live's chain, verbatim. Not trimmed or
   *  case-folded: Live treats it as free text and two devices may differ
   *  only by case. */
  deviceName: string;
  /** 1-based occurrence when `deviceName` appears more than once on the
   *  track. Absent = 1 = the first match. */
  nth?: number;
}

export interface ButtonItem extends FaceOptions {
  id: string;
  kind: 'button';
  x: number; y: number; w: number; h: number;
  /** Spawn-size preset; also clamps freeform resize ([SIZES.S, SIZES.L]).
   *  Older saved items lacking this field default to 'M' at load time. */
  size?: SizePreset;
  ccNumber: number;
  channel: number;
  onValue: number;
  /** The CC value sent on RELEASE / off (2026-08-28). Absent = 0, which is
   *  what the hardcoded constant always sent, so every existing button keeps
   *  its exact behaviour.
   *
   *  Only meaningful for `actionType: 'cc'`. A `noteon` button's off is a real
   *  note-off (`0x80`, velocity 0) and is not a value the author picks; `pc`
   *  has no off at all. Operator ask: "the midi note should have midi off
   *  value, default to 0 so when user lets go of button, the note stops
   *  playing" - the note half is DEC-187's release rules, this field is the CC
   *  half of the same request. */
  offValue?: number;
  color: LayoutColor;
  /** Default `toggle`; v2 items migrate as toggle (preserves existing
   *  behavior — every legacy button latches on tap). */
  mode?: ButtonMode;
  /** Show-mode guard (SD-5 / DEC-072). When true, in SHOW mode the first
   *  tap ARMS the control (2s window) and the second tap fires — a stray
   *  brush can't trigger it mid-set. Absent = false = fires on first tap
   *  as today. Ignored in edit mode (test-firing is deliberate) and, for
   *  v1, in test mode too — the guard is a stage-execution safety, scoped
   *  to performance mode. */
  guard?: boolean;
  /** "Share with mods" (OPMod ask + operator, 2026-09-23). When true the
   *  button is registered on its target OPBridge(s) as a named action keyed
   *  by this item's stable `id`, so mods the streamer grants it to in OPMod
   *  can press it remotely. It makes the button GRANTABLE, never granted -
   *  the per-mod grant is OPMod's. Absent = false. See `modShare/sync.ts`. */
  shareWithMods?: boolean;
  /** Default `rect`. v2 items migrate as rect. */
  shape?: ButtonShape;
  /** Visual rotation in degrees, 0-359. Applied as CSS transform to the
   *  button wrapper so hit-area rotates with the visuals. Default 0.
   *  The Inspector's rotation dial snaps at every 45° unless the author
   *  holds Shift while dragging. */
  rotation?: number;
  /** Default `cc`. When `noteon`, `note` and `velocity` drive the send;
   *  ccNumber is ignored at runtime but kept on the item so toggling
   *  action types is non-destructive. */
  actionType?: ButtonActionType;
  /**
   * Ableton Control steps, fired in order on activate (only used when
   * `actionType === 'ableton'`). An ARRAY rather than a single value
   * because the operator ask was explicitly *"a button that does either or
   * both"* - "this song is 140, 6/8, rec quant off" should be one tap, not
   * three buttons.
   *
   * Every other `actionType` fires exactly one message, so this is the only
   * multi-step kind. It is kept as its own field rather than widening the
   * MIDI fields above so that switching action type stays non-destructive,
   * the same contract `ccNumber` already has when `actionType` is `noteon`.
   *
   * These never emit MIDI. They ride OPBridge's `/osc/send` to the
   * OP-AbletonSync device, EXCEPT the Clock-group steps carrying
   * `clock: 'practice'`, which never leave the browser at all.
   */
  abletonSteps?: AbletonStep[];
  /**
   * The OBS scene this button switches to (only used when
   * `actionType === 'obs'`). OBS's own scene name, verbatim - not
   * normalised, trimmed or case-folded, because OBS treats it as an opaque
   * identifier and two scenes may differ only by case. It is
   * percent-encoded once, at dispatch, and nowhere else.
   *
   * ONE scene, not an array (unlike `abletonSteps`): OBS has exactly one
   * active program scene, so "switch to A and also to B" has no meaning. A
   * button that wants to do several things at once is a chain, and chains
   * are what `obs.scene` being a first-class `ActionStep` leaves room for.
   *
   * Absent or empty = unconfigured, which the button shows on its face and
   * dispatch REFUSES to send (an empty name builds a different, non-existent
   * route). A name that no longer appears in the bridge's scene list is a
   * loud dead state too - the scene was renamed or deleted in OBS - rather
   * than a button that silently 422s mid-set.
   */
  obsScene?: string;
  /**
   * The Ableton device this button toggles (only used when
   * `actionType === 'abletonDevice'`).
   *
   * Absent or incomplete = unconfigured, which the button shows on its face
   * and dispatch REFUSES to send - the same contract `obsScene` has, and for
   * a sharper reason: the OSC target is `127.0.0.1`, which resolves on
   * whatever machine the addressed bridge runs on, so a half-configured send
   * is not an error anywhere in the stack. It is accepted, answered
   * `{ok:true}`, and dropped.
   *
   * See `.notepads/ableton-device-control-plan.md` for the wire and the two
   * sibling asks this waits on.
   */
  abletonDevice?: AbletonDeviceTarget;
  /**
   * The program this button runs (only used when
   * `actionType === 'runProgram'`): its ID in the target bridge's program
   * list (`GET /programs`), verbatim. Never a path or a command line - what
   * runs is whatever the operator registered under that id ON THAT MACHINE,
   * and registering is loopback-only on OPBridge, so a layout cannot make a
   * computer run anything its owner did not put on its list.
   *
   * Per-machine, like `obsScene`: the same id on another bridge is a
   * different program or none, which is why dispatch refuses an unaimed
   * button. Absent or empty = unconfigured, shown on the face and refused.
   */
  runProgramId?: string;
  /**
   * The lighting preset this button plays (only used when
   * `actionType === 'lightPreset'`). Per-machine like `runProgramId`: a
   * preset drives the fixtures patched on the bridge it is sent to, so an
   * unaimed button is refused rather than sent to the default bridge.
   */
  lightPreset?: LightPresetConfig;
  /**
   * The Hue scene this button recalls (only used when `actionType === 'hueScene'`, 2026-10-23, OPBridge S331): the
   * Hue bridge and scene ids, and its room, name and colours as last read, so the face draws it with OPBridge out of
   * reach. Empty `scene` = unconfigured, shown on the face and refused. Fired through a named action (one per scene)
   * on the button's computer; OPBridge recalls it on the PC holding the Hue bridge (`lighting/hueScenes.ts`).
   */
  hueScene?: HueSceneRef;
  /**
   * What this button tells Synesthesia (only used when `actionType === 'synesthesia'`, 2026-11-05, OPBridge S344/S352):
   * a scene by name, a move (next / previous / random / home, a playlist step) or a look (a meta control to a value,
   * optionally faded), and when it lands (`when`, default the next bar). Sent with `POST /synesthesia/fire` through any
   * connected computer; OPBridge sends it on to the PC that drives Synesthesia (`synesthesia/fire.ts`).
   */
  synesthesia?: SynButtonConfig;
  /** MIDI note number 0-127 (only used when actionType === 'noteon'). */
  note?: number;
  /** NoteOn velocity 1-127 (only used when actionType === 'noteon'). */
  velocity?: number;
  /** Program Change number 0-127 (only used when actionType === 'pc').
   *  A PC is a two-byte message with no value and no OFF counterpart, so a
   *  `pc` button always behaves as a trigger regardless of `mode`, shows no
   *  ON/OFF state, and claims no CC allocation slot (`layout/midiAlloc.ts` -
   *  "next free" is meaningless for a program number). It DOES participate
   *  in the layout linter's duplicate check (`performance/lint.ts`, added 2026-08-10)
   *  in its own PC namespace, separate from CC - unlike `PresetChangeItem`,
   *  a plain `pc` button always fires on the rig's default port, so a PC
   *  collision here is never a false positive from two different devices. */
  program?: number;
  /** Which MIDI ports this control sends to. Default [1] (Port 1 only).
   *  Empty array = control doesn't send anywhere (rare; mostly a debug
   *  state). [1, 2] = broadcast to both ports. */
  destinations?: PortId[];
  /** Which registered `GuitarPedal` this button fires on, for a Custom
   *  pedal's preset recall (DEC-152; reworked to a live reference
   *  2026-08-04, see `PresetChangeItem.pedalId`'s doc comment for the full
   *  reasoning). Port AND channel resolve live from the pedal when set —
   *  `channel` above is ignored for a pedal-bound button, kept only so the
   *  field stays required for every other Button use. Absent = an ordinary
   *  button (channel/port come from the fields above / the rig default),
   *  which is every button except a Guitar FX Preset one. A `pedalId`
   *  pointing at a removed pedal is a loud dead state, never a silent
   *  fall-back to Ableton's port. */
  pedalId?: string | undefined;
  /** DEC-154 inbound MIDI trigger - A-path, and the kind DEC-154 named first
   *  ("FireButton `trigger` mode") but which shipped last. Which rule action
   *  this resolves to depends on the button's own mode, and BOTH are
   *  OPBridge-native (see `midiTrigger/ruleSync.ts::actionFor`):
   *    - trigger/momentary -> the same static `midi.sendRaw` bytes
   *      `dispatch.ts::toggleActions` already builds for the ON side, so a
   *      pedal-fired press and a finger tap are identical on the wire;
   *    - toggle -> `{module:'toggle',command:'flip'}` against the toggle this
   *      button ALREADY keeps in OPBridge's library under its own item id
   *      (`FireButton.tsx`'s debounced push), so a pedal flip and a tap share
   *      one server-side state rather than fighting over two.
   *
   *  NOT offered for `actionType: 'ableton'` - an Ableton Control button's
   *  chain can contain practice-clock steps that never leave the browser, so
   *  a rule could only fire part of it. The Inspector hides the Learn block
   *  there rather than authoring a binding that silently half-fires.
   *
   *  See `SequencerItem.midiTrigger`'s doc comment for the shared contract. */
  midiTrigger?: MidiTriggerBinding | undefined;
  /** Which mesh OPBridge(s) this button's command fans out to (2026-08-13,
   *  supersedes DEC-063's primary-only dispatch). IGNORED when `pedalId` is
   *  set — a pedal-bound button inherits its bridge from the pedal
   *  (Connected Devices), same as port/channel. For an ordinary button:
   *  absent = the default bridge, dynamically (tracks Set-as-default), so
   *  most buttons need zero configuration; an explicit empty array means
   *  the operator deliberately selected zero destinations (a real "not
   *  configured" state, never a fault); a populated array sends the same
   *  command to every listed bridge in parallel. */
  bridgeIds?: string[] | undefined;
}

export interface LfoItem extends FaceOptions {
  id: string;
  kind: 'lfo';
  x: number; y: number; w: number; h: number;
  size?: SizePreset;
  ccNumber: number;
  channel: number;
  bars: number;
  min: number;
  max: number;
  waveform: LfoWaveform;
  color: LayoutColor;
  destinations?: PortId[];
  /** DEC-154 inbound MIDI trigger — A-path, `module:"lfo",command:"start"`.
   *  Unlike Sequencer/Macro (id-reference into a separate OPBridge library),
   *  LFO has no library — the rule's action carries the item's CURRENT
   *  ccNumber/channel/bars/min/max/waveform resolved fresh on every sync
   *  (see `midiTrigger/ruleSync.ts`), same live-tracking model, just no
   *  intermediate id lookup. See `SequencerItem.midiTrigger`'s doc comment
   *  for the shared contract. */
  midiTrigger?: MidiTriggerBinding | undefined;
  /** Which mesh OPBridge(s) this LFO's CC output fans out to. Absent = the
   *  default bridge, dynamically; `[]` = deliberately zero destinations
   *  (not a fault); a populated array fans out to every listed bridge.
   *  See ButtonItem.bridgeIds for the full contract. */
  bridgeIds?: string[] | undefined;
}

/** Response-curve shape for a fader.
 *  - `linear` (default): value tracks handle position 1:1.
 *  - `exp`: exponential — handle bottom 50% covers MIDI 0..32 etc.
 *  - `log`: logarithmic — inverse of exp.
 *  v3 schema-only; the InspectorPanel hides the picker until a polish pass. */
export type FaderResponse = 'linear' | 'exp' | 'log';

/**
 * FaderItem — a draggable vertical (or horizontal) fader that sends CC
 * on change. Bipolar mode (`centered: true`) renders the handle as a
 * bidirectional offset from the midpoint, matching TouchOSC pan/pitch
 * faders. Response curve is schema-only in v1 (advanced-tier polish).
 *
 * v1 supports CC only. Pitchbend (the other obvious bipolar candidate)
 * would be a separate `actionType` extension once we wire pitchbend
 * end-to-end on the bridge.
 */
export interface FaderItem extends FaceOptions {
  id: string;
  kind: 'fader';
  x: number; y: number; w: number; h: number;
  size?: SizePreset;
  ccNumber: number;
  channel: number;
  /** Last-sent value (0-127). Persisted so the handle restores at the
   *  same position after a reload. */
  value: number;
  color: LayoutColor;
  /** Bipolar (centered) mode — handle rest position is the middle, drag
   *  up sends 64..127, drag down sends 0..64. Matches TouchOSC's
   *  `centered` flag. Default false. */
  centered?: boolean;
  /** Response curve. Default 'linear'. */
  response?: FaderResponse;
  /** Response shaping factor 1-100. Higher = closer to linear; lower =
   *  more aggressive curve. TouchOSC parity. Only meaningful when
   *  response !== 'linear'. Default 100. */
  responseFactor?: number;
  /** Vertical (true) or horizontal. Default vertical. */
  vertical?: boolean;
  /** Show-mode guard (SD-5): long-press to grab before the fader
   *  responds; a quick brush sends nothing. Absent = false. */
  guard?: boolean;
  destinations?: PortId[];
  /** Which mesh OPBridge(s) this fader's CC output fans out to. See
   *  ButtonItem.bridgeIds for the full contract. */
  bridgeIds?: string[] | undefined;
}

/**
 * ImageItem — a freely-resizable image (PNG/JPG/WebP) on the canvas.
 * Used for backgrounds, decorative graphics, and visual landmarks
 * during live performance ("the green section is verse one").
 *
 * The image bytes live on the bridge filesystem at
 * `%LOCALAPPDATA%\OPButtons\assets\<sha256>.webp` (Windows) or the
 * platform equivalent. The item carries the asset hash + the original
 * dimensions; the PWA resolves the binary via the bridge over WS.
 *
 * Content-addressed: the same image dropped onto two pages stores
 * exactly one file. Hash is sha256 of the *compressed* WebP, so the
 * importer round-trips it deterministically.
 *
 * `fit` mirrors CSS object-fit; default `contain` preserves aspect
 * ratio. Image items render BENEATH other items by default (added at
 * the front of the items[] array). User can promote via z-order
 * controls in the InspectorPanel.
 */
export interface ImageItem extends FaceOptions {
  id: string;
  kind: 'image';
  x: number; y: number; w: number; h: number;
  size?: SizePreset;
  /** sha256 of the stored asset bytes. Resolves via bridge `asset.get`. */
  assetHash: string;
  /** Original image natural dimensions, captured at import for aspect
   *  preservation + default spawn sizing. */
  naturalW: number;
  naturalH: number;
  /** CSS object-fit equivalent. Default 'contain'. */
  fit?: 'contain' | 'cover' | 'fill';
  /** Opacity 0-1. Default 1. */
  alpha?: number;
}

/** How ButtonGrid cells coordinate active state. See ButtonGridItem doc
 *  for behavior of each variant. */
export type ButtonGridSelectionMode = 'radio' | 'radio-required' | 'multi' | 'up-to-n';

/**
 * ButtonGridItem — a rows × cols grid of cells. `selectionMode` controls
 * how many can be active at once and the transition semantics:
 *
 *   'radio' (default, legacy) — exactly one active or none. Tapping a
 *     different cell sends OFF to the previously-active and ON to the
 *     new one. Tapping the active cell sends OFF (all-off).
 *   'radio-required' — always exactly one active. Tap different = swap.
 *     Tap active = no-op (can't turn it off).
 *   'multi' — each cell is an independent toggle. No cross-cell writes.
 *   'up-to-n' — up to `maxActive` cells active at once. When at cap, a
 *     new tap evicts the least-recently-activated cell (oldest wins the
 *     boot).
 *
 * Cell behavior is driven by grid defaults (baseCC, channel, onValue,
 * offValue) with optional per-cell overrides. Cells are auto-numbered
 * baseCC + index (row-major) unless a cell sets its own ccNumber.
 *
 * Visual: cells sit `gap` px apart (default 5; 0 makes one strip). Each
 * cell has a label and a small index number in the top-right corner. Its
 * colour is the grid's one hue (setGridLook clears the cells' own, and a
 * grown grid keeps it) or, in Rainbow, each cell's own `color`. Colour is
 * light, not paint (DEC-271): a lit lower edge at rest, a flood when active.
 *
 * Echo-follows-truth: the lit state comes from incoming MIDI matching
 * (cc, channel) of each cell. The PWA tracks tap intent locally for
 * 'tap-but-not-confirmed' UX, but a cell is only authoritatively ACTIVE
 * once Ableton echoes back.
 */
export interface ButtonGridItem extends FaceOptions {
  id: string;
  kind: 'buttongrid';
  x: number; y: number; w: number; h: number;
  size?: SizePreset;
  rows: number;
  cols: number;
  /** First CC; cell N gets baseCC + N unless overridden. */
  baseCC: number;
  channel: number;
  onValue: number;
  /** OFF value — sent to the previously-active cell when a new one is tapped,
   *  and to the active cell when it's tapped a second time. Default 0. */
  offValue: number;
  /** How many cells can be active at once and how transitions resolve.
   *  Default 'radio' preserves the pre-selectionMode behavior for every
   *  existing saved layout. */
  selectionMode?: ButtonGridSelectionMode;
  /** Cap for 'up-to-n' mode. Ignored in other modes. Default 2. */
  maxActive?: number;
  /** Space between cells, px (operator, 2026-10-15: "i would like to be
   *  able to remove/change that padding"). Absent = 5, the old fixed gap. */
  gap?: number;
  cells: ButtonGridCell[];
  /** Grid-level advanced actions (v1 hides this). When set, fires for the
   *  grid as a whole on any cell activate; per-cell `actions` still apply. */
  actions?: ControlActions;
  /** Show-mode guard (SD-5): first tap on a cell ARMS it (2s), the second
   *  tap fires — a stray brush can't trigger a cell mid-set. Per-cell arm;
   *  item-level flag. Absent = false. */
  guard?: boolean;
  destinations?: PortId[];
  /** Which mesh OPBridge(s) this grid's CC output fans out to. See
   *  ButtonItem.bridgeIds for the full contract. */
  bridgeIds?: string[] | undefined;
  // ---- The button look (DEC-316 / DEC-317, layout/buttonLook.ts). Every field is optional: absent = the decided
  // default, so a saved project loads as is. ----
  /** Lit glass (default), Glass or Pads. */
  finish?: GridFinish;
  /** Where the grid's Name shows, IN LINE with the grid (a band inside it): a corner or centre, up the side, or off.
   *  Default top left. A grid with no Name shows no title. */
  titleAt?: GridTitleAt;
  /** The grid's instrument icon (DEC-128's sprite), or none (default). */
  icon?: InstrumentIconId | 'none';
  /** Where the icon sits: in the title (default), a pad corner, above each name, or off. */
  iconAt?: GridIconAt;
  /** Texture pattern: Auto (from the icon, default), Smooth, or one pattern. */
  texture?: GridTexture;
  /** Texture size: Auto (from the icon's register, default), Fine, Medium, Coarse. */
  texSize?: GridTexSize;
  /** Texture colour: Auto (the grid's, default), Dark (grooves only), or a palette colour. */
  texColour?: 'auto' | 'dark' | Exclude<PaletteHue, 'none'>;
  /** Texture strength, 0-100 %. Default 20. */
  texStrength?: number;
  /** How long a pressed pad SHOWS the wait the receiver keeps before it lights: Now, Beat, Bar, 2 or 4 bars. Visual
   *  only: the press is sent at once (DEC-317). New grids are made with 'bar' (the decided default); absent reads as
   *  'now', so a grid saved before this lights exactly as it did. */
  starts?: GridStarts;
  /** On the beat while the clock runs: Pulse (default), Breathe, Blink or Off. Only when Starts is not Now. */
  beatFlash?: GridBeatFlash;
}

/**
 * SequencerItem — layout placeholder for a step sequencer. The actual
 * sequencer document (lanes × steps) lives in the global sequencer
 * library (opbuttons-sequencers-v1) keyed by `sequencerId`; this item is
 * just where the sequencer renders on a page.
 *
 * Why split: a sequencer is heavy (4-16 lanes × 16-64 steps × per-step
 * velocity/probability/etc.) and shouldn't be inlined into every page
 * that references it. Same model as Macro: layout points by id, the
 * document lives elsewhere. v0.x has one sequencer per item; future
 * versions may allow multi-pattern banks.
 *
 * advanced field `actions?` is reserved per the basic-or-advanced rule;
 * the lane-level action surface lives inside SequencerLane itself (in the
 * sequencer document), so item-level actions are purely for triggers
 * fired by the sequencer's lifecycle (e.g. "when sequencer completes,
 * do X") — none of which v1 exposes.
 */
export interface SequencerItem extends FaceOptions {
  id: string;
  kind: 'sequencer';
  x: number; y: number; w: number; h: number;
  size?: SizePreset;
  /** Id into the SequencerLibrary's `sequencers[]`. */
  sequencerId: string;
  /** Editor display color, mirrors LayoutColor. */
  color: LayoutColor;
  /** RESERVED — see ControlActions note above. */
  actions?: ControlActions;
  /** DEC-154 inbound MIDI trigger — first slice, Sequencer arm only.
   *  Absent = no pedal/device binding. See `MidiTriggerBinding`. `| undefined`
   *  is explicit (not just `?`) so a patch can clear an existing binding —
   *  `exactOptionalPropertyTypes` otherwise rejects `{ midiTrigger: undefined }`
   *  as a type error, not just an omitted key. */
  midiTrigger?: MidiTriggerBinding | undefined;
  /** Which mesh OPBridge(s) this sequencer's engine push/arm targets. See
   *  ButtonItem.bridgeIds for the full contract. */
  bridgeIds?: string[] | undefined;
}

/**
 * DEC-154 — inbound MIDI trigger binding. Captured once at Learn time from
 * `GET /midi/recent` (filtered to the fixed `OPButtons-Control` port — see
 * `midiTrigger/constants.ts`) and stored verbatim; never re-resolved. This
 * is the item-owned half of the design — OPButtons owns the binding, an
 * OPBridge rule is a PROJECTION pushed from it, never read back.
 *
 * `path` is fixed per item KIND, not authored per-binding (DEC-154's A/B
 * split is about what the item's action needs, not a per-user choice) —
 * Sequencer/Macro/LFO arm-or-start are always 'a' (OPBridge's own native
 * rule-action modules fire them with the tab closed). Kept on the binding
 * itself (not derived at every call site) so the badge can render from a
 * single field without re-deriving the item-kind → path mapping.
 */
export interface MidiTriggerBinding {
  /** Exact port string from `/midi/recent` at Learn time (loopMIDI port
   *  names are stable) — always `OPButtons-Control` today, kept verbatim
   *  rather than hardcoded so a future non-default port needs no schema
   *  change. */
  port: string;
  match: {
    type: 'note' | 'cc';
    /** Note number (0-127) or CC number (0-127), per `type`. */
    number: number;
    channel: number;
  };
  path: 'a' | 'b';
}

/**
 * MacroItem — layout placeholder for a macro tile. The macro document
 * (lanes × shapes × MIDI targets) lives in the global macro library
 * (opbuttons-macros-v1) keyed by `macroId`; this item is just where that
 * macro renders on a page.
 *
 * Same id-only model as SequencerItem — pages can reference shared
 * macros, the library outlives any individual project. The `⋯` popover
 * opens MacrosEditor (full-modal) since macro authoring won't fit in
 * an inline form.
 *
 * `actions?` is reserved for future lifecycle-trigger work (e.g. "when
 * this macro finishes, fire X") consistent with the basic-or-advanced
 * rule across every other item.
 */
export interface MacroItem extends FaceOptions {
  id: string;
  kind: 'macro';
  x: number; y: number; w: number; h: number;
  size?: SizePreset;
  /** Id into the MacroLibrary's `macros[]`. */
  macroId: string;
  color: LayoutColor;
  actions?: ControlActions;
  /** DEC-154 inbound MIDI trigger — A-path, `module:"macro",command:"arm"`.
   *  See `SequencerItem.midiTrigger`'s doc comment for the full contract;
   *  identical shape, different engine module. */
  midiTrigger?: MidiTriggerBinding | undefined;
  /** Which mesh OPBridge(s) this macro's engine push/arm targets. See
   *  ButtonItem.bridgeIds for the full contract. */
  bridgeIds?: string[] | undefined;
}

/**
 * StreamDeckItem — first-class layout item that fires a real, already-
 * configured Elgato Stream Deck action (an OBS scene, a Hue scene, a
 * hotkey — whatever sits on that key).
 *
 * Addressing model, REWRITTEN 2026-07-28. This used to carry `key: number`
 * on the assumption that you press a key BY INDEX. That premise was wrong
 * twice over: the Elgato plugin SDK has no command that invokes an action
 * at all, and the mechanism that DOES work — Elgato's MCP Deck, the same
 * surface Aitum Nexus uses — identifies each action by a **UUID per action
 * instance**, not by a grid position. So the field is `actionId`.
 *
 * Path: OPButtons → OPBridge `POST /streamdeck/execute` → the local
 * `@elgato/mcp-server` (HTTP :9090) → named pipe → Stream Deck app. It must
 * route through OPBridge: the MCP server 500s on any request carrying an
 * `Origin` header, so a browser on the iPad cannot call it directly.
 *
 * Only actions the operator has placed in Stream Deck's **MCP Actions**
 * profile are addressable — an explicit allowlist on their side, which is
 * why the Inspector picker is populated from OPBridge rather than typed.
 */
export interface StreamDeckItem extends FaceOptions {
  id: string;
  kind: 'streamdeck';
  x: number; y: number; w: number; h: number;
  size?: SizePreset;
  /** MCP action instance UUID, as reported by `GET /streamdeck/actions`.
   *  Absent = unassigned; the item renders as such and refuses to fire
   *  rather than firing something arbitrary. */
  actionId?: string;
  /** Human title cached at pick time, so the face still reads correctly
   *  when OPBridge or the MCP server is down and the list can't be
   *  fetched. Display only — `actionId` is the identity. */
  actionTitle?: string;
  /** Optional display label override. Falls back to `actionTitle`.
   *  Distinct from FaceOptions.name — `label` shows in the pill;
   *  `name` is the bigger title underneath. */
  label?: string;
  color: LayoutColor;
  /** RESERVED — see ControlActions note above. */
  actions?: ControlActions;
  /** @deprecated Pre-2026-07-28 key index. Never had any effect (the
   *  bridge-side client was a stub that logged and resolved success).
   *  Retained only so old persisted layouts still parse. */
  key?: number;
  /** Which mesh OPBridge(s) this Stream Deck action fans out to — matters
   *  since the MCP server only runs on whichever machine has the physical
   *  Stream Deck plugged in. See ButtonItem.bridgeIds for the full
   *  contract. */
  bridgeIds?: string[] | undefined;
}

/** Source of the incoming signal that drives a StatusLed's on/off state.
 *  v1 supports MIDI CC and Note-On events. OSC is reserved for when the
 *  PWA can subscribe to arbitrary bridge addresses (needs the Rules /
 *  peer-sync surface). */
export type StatusLedSourceType = 'midi.cc' | 'midi.note';

/** Visual shape for the LED indicator. Kept intentionally narrower than
 *  ButtonShape — LEDs are indicators, not affordances, so triangles /
 *  arrows / diamonds would only confuse readers. Circle default; rect
 *  and pill included because rack-style status strips read as short
 *  rectangles. */
export type StatusLedShape = 'circle' | 'rect' | 'pill';

/**
 * StatusLedItem — read-only indicator lamp that lights up when an
 * incoming MIDI event matches its binding. NEVER sends. Composes with the
 * (future) Rules engine for the "which signal drives this" wiring.
 *
 * Use cases: "is this Ableton track armed?" (bind to the CC Ableton sends
 * when arm flips), "is this VST enabled?" (bind to CC threshold), "is
 * the drum sequencer currently on the down-beat?" (bind to a note pulse).
 *
 * Behavior: subscribe to incoming MIDI. When a message matches (channel,
 * cc-or-note number) with value >= threshold, LED = ON. When value <
 * threshold (or note-off), LED = OFF. No latching, no debounce — pure
 * echo-follows-truth like ButtonItem's lit state.
 *
 * Shape uses a narrower set than ButtonShape (circle / rect / pill) — an
 * LED shaped like a triangle would confuse the affordance ("do I press
 * it?"). Rotation not exposed on LEDs for the same reason.
 */
export interface StatusLedItem extends FaceOptions {
  id: string;
  kind: 'statusLed';
  x: number; y: number; w: number; h: number;
  size?: SizePreset;
  /** Palette token for the ON state. OFF is a dim variant of the same. */
  color: LayoutColor;
  /** What kind of MIDI event drives the state. */
  sourceType: StatusLedSourceType;
  /** MIDI channel 0-15. */
  channel: number;
  /** CC number 0-127 (only used when sourceType === 'midi.cc'). */
  ccNumber?: number;
  /** Note number 0-127 (only used when sourceType === 'midi.note'). */
  note?: number;
  /** Value threshold for ON. Incoming value >= threshold turns the LED
   *  on; value < threshold turns it off. Default 1 (any non-zero = on),
   *  which matches how DAWs typically send toggle-CCs (0 = off, 127 =
   *  on). Authors tweak this for VSTs that report analog-ish values. */
  threshold: number;
  /** Visual shape. Default `circle`. */
  shape?: StatusLedShape;
  /** RESERVED — which mesh bridge(s) to listen on (2026-08-14: shape
   *  updated to multi-select, matching the output items' `bridgeIds`,
   *  but still genuinely unpopulated — this is a bigger gap than a
   *  schema field). Inbound MIDI (this item's whole reason to exist) has
   *  NO live source at all (2026-08-20: the legacy OPButtonsBridge WS this
   *  used to flow through is deleted, confirmed unused for months) —
   *  `midi/inbound.ts` is the real production home, currently inert.
   *  `opbridge/consumerWs.ts` carries only control messages (inheritance/
   *  revocation), never MIDI-in; OPBridge's own general `/ws` DOES already
   *  forward it (found 2026-08-20, re-flagged in the cross-project ask),
   *  pending an answer on whether the PWA can use that directly. Do not
   *  build a picker UI for this field without the real data behind it, or
   *  it becomes a dead control (DEC-138). */
  bridgeIds?: string[];
}

/** Which live source a DisplayItem reads. Chosen once at authoring time
 *  (in the Inspector) and sticky - a DISPLAY item is bound to exactly one
 *  source, same binding model as StatusLed / Mixer / WavesAutotune. Not a
 *  multi-page/trigger-cycled display. */
export type DisplaySourceType =
  | 'bpm' | 'lfo' | 'helix' | 'timeline' | 'button' | 'transpose' | 'capo' | 'key'
  // Ableton Control readouts (2026-08-27). `timesig` and `transport` follow
  // the ACTIVE clock (live when slaved, practice otherwise), matching `bpm`.
  // The other three are Ableton-session properties with no local equivalent,
  // read straight off the device observations OPBridge relays.
  | 'timesig' | 'transport' | 'recquant' | 'launchquant' | 'metronome'
  // The song timeline playing on any computer (2026-10-26, DEC-315): no binding, read from `timeline/transport.ts`.
  | 'nowplaying';

/** Beats of countdown a `nowplaying` Display shows before a section changes (DEC-315). Absent = 4. */
export type DisplayCountdownBeats = 4 | 8 | 16;

/** Visual treatment. `'skin'` (default, absent = 'skin') follows the
 *  app's active skin via the shared 24-token contract (DEC-121) like
 *  every other item. `'lcd'` is a single fixed green-on-black look that
 *  intentionally ignores the active skin - a deliberate, documented
 *  exception (see skin-components.css), not a bug - same per-item
 *  override pattern as Mixer's `skin` field (DEC-107). */
/** Skin, LCD (the fixed green), or Glass (the screen lit in its colour, DEC-319). */
export type DisplayStyle = 'skin' | 'lcd' | 'glass';

/**
 * DisplayItem - read-only, 3-line glanceable text readout modeled on a
 * small LCD ("second iPad glance-view over Ableton" use case). NEVER
 * sends. One item = one bound source, picked in the Inspector.
 *
 * Two flavors of source:
 * - **Per-item binding** (`lfo`, `timeline`, `helix`, `button`): points at
 *   ONE specific other item/pedal on the page via an `xxxId` field, picked
 *   in the Inspector. A missing/removed reference is a loud dead state
 *   (DEC-138), never a stale frozen value.
 * - **Session-global, no binding** (`bpm`, `transpose`, `capo`, `key`, `nowplaying`):
 *   there is only ever ONE of these per session (`transpose/session.ts`'s
 *   concert transpose, `autotune/session.ts`'s key+scale), so the Display
 *   just reads the shared module-level signal directly - no picker, no
 *   `xxxId` field, same reasoning as why a Transpose/Capo/WavesAutotune row
 *   itself has no per-item "current value."
 *
 * Two known v1 limitations, intentional, not bugs to reflexively revisit:
 * - `source: 'helix'` shows the last preset/snapshot COMMANDED BY
 *   OPBUTTONS, not the pedal's live state - no MIDI-in feedback path
 *   exists yet (Windows driver bug on the dev PC, untested on Mac). A
 *   footswitch change made directly on the unit will NOT be reflected.
 * - `source: 'timeline'` binds to one specific TimelineLauncherItem on
 *   the page (via `timelineLauncherId`), not "the timeline system" in
 *   the abstract. If that item is deleted, or a page has more than one,
 *   this DISPLAY only ever reflects the instance it's pointed at.
 */
export interface DisplayItem extends FaceOptions {
  id: string;
  kind: 'display';
  x: number; y: number; w: number; h: number;
  size?: SizePreset;
  color: LayoutColor;
  /** 'skin' | 'lcd'. Absent = 'skin'. */
  style?: DisplayStyle;
  /** Which live source this instance reads. */
  source: DisplaySourceType;
  /** Bound LFO item id (source === 'lfo'). */
  lfoId?: string;
  /** Bound TimelineLauncher item id (source === 'timeline'). */
  timelineLauncherId?: string;
  /** Bound GuitarPedal id (source === 'helix'). */
  pedalId?: string;
  /** Bound Button item id (source === 'button'). */
  buttonId?: string;
  /** source === 'nowplaying': beats of countdown before a section changes. Absent = 4. */
  countdownBeats?: DisplayCountdownBeats;
  // ---- Displays are screens (DEC-319, layout/displayLook.ts). All optional: absent = the decided default. ----
  /** Where the bezel's label (what it shows) sits: a corner or centre, top or bottom, or off. Default top left. */
  labelAt?: DisplayLabelAt;
  /** The kind's icon in the label. Default on. */
  labelIcon?: boolean;
  /** The big line: left (default) or centre. */
  align?: DisplayAlign;
  /** A timeline's name line: left (default), centre or right. */
  songAlign?: DisplaySongAlign;
  /** The detail line under the big one. Default on. */
  showDetail?: boolean;
  /** Beat dots beside the big line, while the clock runs. Default off. */
  showDots?: boolean;
  /** Glow on the screen's text, 0-100 %. Default 100. */
  glow?: number;
}

/**
 * XYPadItem — 2D touch surface (Kaoss-style). Sends a continuous CC on
 * each axis. Puck follows the finger during touch; on release, either
 * stays at last position (`returnToCenter: false`, default) or springs
 * back to center (`returnToCenter: true`) for a Kaoss-Pad-style momentary
 * effect.
 *
 * Wire behaviour is two independent CC streams — one per axis — so the
 * pad works with any DAW that can bind each CC to a different destination
 * without needing a specific pad-aware protocol.
 */
export interface XYPadItem extends FaceOptions {
  id: string;
  kind: 'xypad';
  x: number; y: number; w: number; h: number;
  size?: SizePreset;
  color: LayoutColor;
  /** X-axis CC number 0-127. */
  xCcNumber: number;
  /** X-axis MIDI channel 0-15. */
  xChannel: number;
  /** Y-axis CC number 0-127. */
  yCcNumber: number;
  /** Y-axis MIDI channel 0-15. */
  yChannel: number;
  /** Last-sent X value (0-127). Persisted so the puck restores at the
   *  same position after a reload. */
  xValue: number;
  /** Last-sent Y value (0-127). Persisted so the puck restores at the
   *  same position after a reload. */
  yValue: number;
  /** When true, releasing the pad snaps the puck (and values) back to
   *  center (64, 64). Matches the classic Kaoss Pad momentary behavior.
   *  Default false — puck stays where the finger left it. */
  returnToCenter?: boolean;
  /** Invert Y axis so up = higher value. TouchOSC-style default (true).
   *  Some DAWs prefer up = lower (envelope-style). */
  yInverted?: boolean;
  /** Show-mode guard (SD-5): long-press to grab before the pad responds;
   *  a quick brush sends nothing. Absent = false. */
  guard?: boolean;
  destinations?: PortId[];
  /** Which mesh OPBridge(s) this pad's dual-axis CC output fans out to.
   *  See ButtonItem.bridgeIds for the full contract. */
  bridgeIds?: string[] | undefined;
}

/** Timeline Launcher render mode. `whole` = one big launch button.
 *  `whole+sections` = a launch-whole button plus one button per section
 *  (best when the timeline has 2-6 sections). `sections` = one button
 *  per section only, no whole-timeline launch. */
export type TimelineLauncherMode = 'whole' | 'whole+sections' | 'sections';

/** When a launch fires relative to the musical grid.
 *  - `instant`  — fire the moment the button is pressed.
 *  - `nextBar`  — wait for the next whole-bar boundary (quantized launch,
 *    matches the timeline engine's section-jump quantize, DEC-025).
 *  Mutually exclusive (radio). Default `nextBar`. */
export type TimelineLaunchTrigger = 'instant' | 'nextBar';

/** Whether a launched timeline/section loops. `inherit` follows the
 *  timeline's own loop toggle (Timeline.loop); `on`/`off` override it. This is
 *  the "selectable on trigger" loop control. Default `inherit`. */
export type TimelineLaunchLoop = 'inherit' | 'on' | 'off';

/**
 * TimelineLauncherItem — placeholder-shape item for launching a timeline
 * or any of its named sections. UI shape depends on `mode`:
 *
 *   - `whole`           → single big launch button, fires `timeline.start(id)`.
 *   - `whole+sections`  → launch-whole + one button per section.
 *   - `sections`        → one button per section, no whole-timeline launch.
 *
 * Section buttons fire `timeline.jumpToSection(id, sectionId)` — the
 * OPBridge timeline engine (Phase M2, `opbridge-timelines` crate) will
 * quantize the jump to the next bar per DEC-025. Until that engine
 * ships, this item renders + persists but doesn't dispatch (placeholder
 * behavior, same shape as StreamDeckItem's stub mode).
 */
export interface TimelineLauncherItem extends FaceOptions {
  id: string;
  kind: 'timelineLauncher';
  x: number; y: number; w: number; h: number;
  size?: SizePreset;
  color: LayoutColor;
  /** Which timeline in the library this launches. Empty string when the
   *  user hasn't picked yet — item renders a "choose a timeline" prompt
   *  in that state. */
  timelineId: string;
  /** Render shape (see TimelineLauncherMode). Default `whole+sections`. */
  mode?: TimelineLauncherMode;
  /** Launch timing — instant vs quantized to the next bar (see
   *  TimelineLaunchTrigger). Default `nextBar`. */
  trigger?: TimelineLaunchTrigger;
  /** Loop behaviour of the launched timeline/section (see TimelineLaunchLoop).
   *  `inherit` (default) follows the timeline's own loop toggle. */
  loop?: TimelineLaunchLoop;
  /** Which mesh OPBridge(s) this launcher's timeline commands fan out to.
   *  See ButtonItem.bridgeIds for the full contract. */
  bridgeIds?: string[] | undefined;
}

/** How a piano key responds to touch.
 *  - `momentary` — press = Note On, release = Note Off (default; matches
 *    a physical piano).
 *  - `toggle`    — press = Note On, press again = Note Off. Chord-grab
 *    pattern; useful for holding a pad or bass note between fingers.
 *  - `trigger`   — press = Note On immediately followed by Note Off.
 *    Good for percussion samples and one-shots. */
export type PianoTouchMode = 'momentary' | 'toggle' | 'trigger';

/**
 * PianoItem — a virtual keyboard, 1-3 octaves. Sends Note On / Note Off
 * on a fixed MIDI channel with a fixed velocity. Multi-touch supported —
 * one Note per finger, no arbitrary limits.
 *
 * White keys render full-height; black keys overlay at ~60% height. Root
 * octave chooses which MIDI octave sits leftmost (Ableton default = C3
 * for middle C). The piano fits its item box: white keys share the width
 * equally, black keys sit at the standard piano gap positions.
 *
 * `mode` controls key response — momentary (default), toggle, or trigger.
 * Toggle keys keep an internal on/off state per key so a second tap
 * releases the held note. Trigger fires Note On + Note Off back-to-back
 * so a percussion sample plays once regardless of hold time.
 */
export interface PianoItem extends FaceOptions {
  id: string;
  kind: 'piano';
  x: number; y: number; w: number; h: number;
  size?: SizePreset;
  color: LayoutColor;
  /** Octave count 1-3. Fresh spawn defaults to 2. */
  octaves: number;
  /** MIDI octave of the leftmost C. Ableton convention: middle C = C3,
   *  so `rootOctave: 3` puts C3 at the leftmost key. Default 3. */
  rootOctave: number;
  /** MIDI channel 0-15. */
  channel: number;
  /** Fixed velocity 1-127. Default 100. Touch-pressure velocity is a
   *  V2 polish item (needs Pointer Events pressure API). */
  velocity: number;
  /** Touch mode. Default `momentary`. */
  mode?: PianoTouchMode;
  destinations?: PortId[];
  /** Which mesh OPBridge(s) this keyboard's Note On/Off output fans out
   *  to. See ButtonItem.bridgeIds for the full contract. */
  bridgeIds?: string[] | undefined;
}

/**
 * TransposeItem — a row of semitone buttons that set the ONE Ableton
 * transpose via the OP-AbletonTranspose Max for Live device. Like a
 * ButtonGrid, but instead of MIDI it dispatches OSC
 * `/OP-AbletonTranspose/transpose <int>` through OPBridge's `POST /osc/send`
 * to the device's udpreceive on 127.0.0.1:11220.
 *
 * Radio-style: exactly one semitone is active. The active value is the
 * session-wide concert transpose (`transpose/session.ts`), NOT stored on the
 * item — a Transpose row and any Capo rows are linked views of the same
 * value. `start`/`end` bound the offered range (e.g. -5..+7; always spans 0).
 */
export interface TransposeItem extends FaceOptions {
  id: string;
  kind: 'transpose';
  x: number; y: number; w: number; h: number;
  size?: SizePreset;
  /** Lowest semitone offered (clamped -12..0). Default -5. */
  start: number;
  /** Highest semitone offered (clamped 0..+12). Default +7. */
  end: number;
  /** Show-mode guard (SD-5): first tap on a semitone ARMS it (2s), the
   *  second tap sets + sends the transpose. Absent = false. */
  guard?: boolean;
  color: LayoutColor;
  /** Which mesh OPBridge(s) this row's OSC transpose command fans out to.
   *  See ButtonItem.bridgeIds for the full contract. */
  bridgeIds?: string[] | undefined;
}

/**
 * CapoItem — a row of capo-fret buttons: a guitar-side view of the SAME
 * concert transpose a TransposeItem drives, offset by the global drop
 * tuning (`capo = concertTranspose - dropTuning`, i.e. transpose + |drop|).
 * Pressing a fret sets the concert transpose to `fret + dropTuning` and
 * dispatches it, so Capo and Transpose rows stay in lock-step. Range is
 * 0..+11 (default 0..+7) — a capo can't be a negative fret. Drop tuning
 * (0..-4; -1 = Eb) is a global session setting edited on the Capo inspector.
 */
export interface CapoItem extends FaceOptions {
  id: string;
  kind: 'capo';
  x: number; y: number; w: number; h: number;
  size?: SizePreset;
  /** Lowest capo fret offered (clamped 0..+11). Default 0 (open). */
  start: number;
  /** Highest capo fret offered (clamped 0..+11). Default +7. */
  end: number;
  /** Show-mode guard (SD-5): first tap on a fret ARMS it (2s), the second
   *  tap sets + sends the transpose. Absent = false. */
  guard?: boolean;
  color: LayoutColor;
  /** Which mesh OPBridge(s) this row's OSC transpose command fans out to.
   *  See ButtonItem.bridgeIds for the full contract. */
  bridgeIds?: string[] | undefined;
}

/**
 * RecQuantItem - a row of Live RECORD-quantization buttons.
 *
 * Same primitive as Transpose/Capo (a row of pads, one lit), but the value
 * is a LABEL out of a fixed nine-member vocabulary rather than a number, so
 * this item carries an explicit ordered `labels` list instead of a
 * start/end range. The operator's own set is not contiguous in Live's enum
 * order (`0 1/8 1/8T 1/16 1/16T 1/32` skips 1/4 and both combo values), and
 * a range model cannot express that.
 *
 * ⚠️ RECORD quantization, NOT global launch quantization. They are two
 * different enums that share most of their label text - see
 * `abletonRecQuant.ts` for the full warning. This item only ever touches
 * `REC_QUANT_LABELS`; a launch-quant row would be a SEPARATE item kind
 * reading the other module, never this one with a vocabulary switch.
 *
 * WRITE: `POST /osc/send` -> `/OP-BridgeSync/set_rec_quant <label>` at
 * 127.0.0.1:11210, the same spine every other Ableton control uses
 * (OPBridge's `/admin/clock/rec-quant` is admin-loopback only and
 * deliberately not for consumers - their DEC-547).
 *
 * READ: `abletonState().recQuant` off the `/clock/status` poll. The lit cell
 * is ALWAYS the device readback, never local optimism - an optimistic
 * highlight desyncs the moment someone changes the menu in Live itself and
 * then lies with full confidence. A send that the readback has not confirmed
 * within `REC_QUANT_CONFIRM_MS` shows as pending-unconfirmed instead, which
 * is what "the M4L device is not loaded" looks like from here.
 */
export interface RecQuantItem extends FaceOptions {
  id: string;
  kind: 'recQuant';
  x: number; y: number; w: number; h: number;
  size?: SizePreset;
  /** Which record-quantization labels this row offers, in the order drawn.
   *  Members of `REC_QUANT_LABELS`; the Inspector keeps them in Live's own
   *  enum order so the row never reorders under the operator. Never empty -
   *  `setRecQuantLabels` refuses to clear the last one. */
  labels: RecQuantLabel[];
  /** Show-mode guard (SD-5): first tap on a cell ARMS it (2s), the second
   *  tap sends. Absent = false. */
  guard?: boolean;
  color: LayoutColor;
  /** Which mesh OPBridge(s) this row's OSC fans out to. See
   *  ButtonItem.bridgeIds for the full contract. */
  bridgeIds?: string[] | undefined;
  /** Which quantizer the row drives. Absent = `'live'`, Live's own hard
   *  record quantize, so a row authored before OP-Quantize existed keeps
   *  doing what it did until the operator switches it - the device sets
   *  Live's own to None on load, so the two must never both be driven.
   *  `'op-quantize'`: the soft OP-Quantize device, plus an Amount band. */
  quantizer?: RecQuantVia;
  /** OP-Quantize only. The grid label last SENT. OP-Quantize reports to
   *  OPBridge, which does not pass it on yet, so this is what the row shows
   *  until it can read the device back - drawn as "sent", never as "on". */
  sentLabel?: RecQuantLabel;
  /** OP-Quantize only. Amount last sent, 0-100 percent. Absent = never
   *  sent from here (shown as --, not as the device's default). */
  amount?: number;
}

/**
 * AbletonRowItem - an Ableton Control as a FULL ROW (DEC-291): every value
 * of one control as its own cell. Tempo presets, time signatures, Play / Stop
 * / Continue, launch quantize values, or the practice clock's Play / Stop.
 *
 * Each cell IS an Ableton Control step, exactly what a one-step Ableton
 * Control button carries (DEC-180), so a tap fires through the same dispatch
 * and lights through the same readback (`ableton/stepState.ts`), and the
 * Inspector converts a button into a row and back without translating.
 * Every cell is the row's own step type (`ROW_STEP_TYPE[control]`), kept so
 * by `normaliseRowCells`; a tempo or time-signature row's cells share one
 * clock.
 *
 * Record quantize's row is NOT this kind: it is `RecQuantItem`, kept whole
 * (OP-Quantize, Amount, the Live-at-None warning). A launch-quantize row is
 * this kind with launch-quantize steps only, so the two vocabularies still
 * never meet in one item (DEC-181).
 */
export interface AbletonRowItem extends FaceOptions {
  id: string;
  kind: 'abletonRow';
  x: number; y: number; w: number; h: number;
  size?: SizePreset;
  control: AbletonRowControl;
  /** The cells, in the order drawn (canonical order, never tap order).
   *  Never empty - the setter refuses to clear the last one. */
  cells: AbletonStep[];
  /** Show-mode guard (SD-5): first tap on a cell ARMS it (2s), the second
   *  tap sends. Absent = false. */
  guard?: boolean;
  color: LayoutColor;
  /** Which mesh OPBridge(s) the row's sends fan out to. See
   *  ButtonItem.bridgeIds for the full contract. */
  bridgeIds?: string[] | undefined;
}

/** The row's default vocabulary subset (operator's choice, 2026-09-10):
 *  None, 1/8, 1/8T, 1/16, 1/16T, 1/32. Deliberately not all nine - 1/4 and
 *  the two combo values are available in the Inspector but off by default,
 *  because canvas width on the iPad is the scarce resource. */
export const DEFAULT_REC_QUANT_LABELS: RecQuantLabel[] =
  ['0', '8', '8T', '16', '16T', '32'];

/** How long a send has to be confirmed by the device readback before the
 *  cell stops claiming to be in flight. Matches the 2.5s OPBridge's own
 *  dashboard control uses (DEC-547) - same device, same round trip. */
export const REC_QUANT_CONFIRM_MS = 2500;

/**
 * SceneCopyItem - two vertical columns of scene slots (COPY on the left,
 * PASTE on the right) driving the OP-AbletonSceneCopy device over OSC.
 *
 * WHAT THE DEVICE DOES: copies a whole row of Session clips from one scene
 * into another WITHOUT duplicating the scene. Live's own "Duplicate Scene"
 * INSERTS a scene, which renumbers everything below it and breaks per-scene
 * MIDI mappings; this device only writes into clip slots that already exist,
 * so scene count and order are invariant and scene-launch mappings survive.
 *
 * CLIPBOARD MODEL, and why this item is two columns rather than pairs: COPY
 * arms a scene into the device's clipboard, PASTE writes the armed row into
 * a scene. They are two halves of one stateful operation - "COPY 3" then
 * "PASTE 5" moves a row - so the natural surface is two parallel columns,
 * not ten independent buttons. The clipboard is a property of the DEVICE,
 * so it is shared session state here too (`sceneCopy/session.ts`), and every
 * SceneCopy item on the page lights the same armed slot.
 *
 * NUMBERING - the one offset worth being careful about. `firstScene` and the
 * button labels are 1-BASED, matching the scene numbers Live actually draws.
 * The OSC wire is 0-BASED, matching the LOM. `sendSceneCopy` is the only
 * bridge between them, exactly as the device's own `disp()` is on its side.
 *
 * `guard` defaults to TRUE for this kind, unlike Transpose/Capo where it is
 * opt-in. PASTE overwrites clips in the live set and there is no undo path
 * back through OSC - it is the most destructive control in the app, so the
 * two-tap arm is the default rather than a setting the operator has to know
 * to find.
 */
/**
 * Columns a SceneCopy item draws: COPY | PASTE | CLEAR.
 *
 * Named rather than inlined because FOUR places have to agree or the item
 * renders wrong: the CSS grid, `addSceneCopy`, `setSceneCopyRange`, and the
 * size-preset recompute. It went 2 -> 3 when device v0.4 added `clear_scene`,
 * and `migrateV4toV5` exists precisely because items persisted at 2 would
 * otherwise squeeze three columns into a two-column box.
 */
export const SCENE_COPY_COLUMNS = 3;

export interface SceneCopyItem extends FaceOptions {
  id: string;
  kind: 'sceneCopy';
  x: number; y: number; w: number; h: number;
  size?: SizePreset;
  /** Live scene number the FIRST slot addresses, 1-based as Live draws it.
   *  Default 1. The device's own panel is hardwired to scenes 1-5, but the
   *  OSC verbs take any index, so banking further up the set is free here. */
  firstScene: number;
  /** How many slot rows to draw (1..8). Default 5, matching the device's
   *  own SLOT_COUNT. */
  count: number;
  /** Show-mode guard (SD-5): first tap ARMS the slot (2s), second fires.
   *  Absent = TRUE for this kind - see the destructiveness note above. */
  guard?: boolean;
  /**
   * Draw the full-width DELETE ALL row under the slot grid (device v1.2).
   *
   * OFF unless explicitly turned on, and that default is a safety decision
   * as much as a layout one. DELETE ALL empties every clip slot on every
   * scene - the single most destructive action reachable from this app - so
   * an app update must not silently add it to a stage surface nobody asked
   * for it on. Same opt-in reasoning as `WavesAutotuneItem.showStrength`
   * (an update never re-flows an already-authored gig page), with the
   * destructiveness on top.
   *
   * The two-press confirm is NOT configurable and does not read this flag:
   * it lives in the device, where the panel button, a MIDI mapping and our
   * relayed packet all converge. See `SCENE_CLEAR_ALL_OSC_ADDRESS`.
   */
  showDeleteAll?: boolean;
  color: LayoutColor;
  /** Which mesh OPBridge(s) this item's OSC fans out to.
   *  See ButtonItem.bridgeIds for the full contract. */
  bridgeIds?: string[] | undefined;
}

/**
 * WavesAutotuneItem - a composite key + scale selector that MIDI-learns a
 * Waves autotune plugin. Two horizontal key rows plus a MIN / MAJ / Chrom
 * scale-type row. Sends plain MIDI CC (channel + value tables live in
 * `autotune/session.ts`), NOT OSC - so unlike Transpose/Capo it needs the
 * primary bridge's default MIDI output port.
 *
 * Independent of the concert transpose: selecting a key here doesn't move
 * Ableton's transpose and vice-versa (user decision 2026-07-16). The two
 * key rows are ergonomic alternate layouts of the SAME single key
 * selection - row 1 is anchored on `row1Home` (default A -> "Am"), row 2 on
 * `row2Home` (default E -> "Em"), so both preferred roots sit at the home
 * column. Tapping a pitch class in either row sends the identical CC 55
 * value; the active key lights wherever it appears in both rows.
 *
 * `start`/`end` are semitone offsets from each row's home (range always
 * spans 0 = the home/root), same adjustable-ends model as TransposeItem.
 * Width derives from cell size × key count; height is three rows.
 */
/** Where the MIN/MAJ/Chrom scale row sits relative to the two key rows.
 *  `bottom` (default) / `top` stack it as a full-width row (3 rows tall).
 *  `left` / `right` put it as a vertical column beside the key rows - more
 *  compact: the item is only 2 key-rows tall and the 3 scale buttons each
 *  take 2/3 of a row height (3 fit into 2). */
export type WavesAutotuneScalePlacement = 'bottom' | 'top' | 'left' | 'right';

export interface WavesAutotuneItem extends FaceOptions {
  id: string;
  kind: 'wavesAutotune';
  x: number; y: number; w: number; h: number;
  size?: SizePreset;
  /** Lowest semitone offset from each row's home (clamped -12..0). Default -5. */
  start: number;
  /** Highest semitone offset from each row's home (clamped 0..+12). Default +7. */
  end: number;
  /** Pitch class 0-11 of the top row's home/root. Default 9 (A → "Am"). */
  row1Home: number;
  /** Pitch class 0-11 of the bottom row's home/root. Default 4 (E → "Em"). */
  row2Home: number;
  /** MIDI channel 1-16 the Waves plugin listens on. Default 2. The key +
   *  scale CC numbers are fixed (in autotune/session.ts), so changing this
   *  is what moves the (channel, cc) pair - hence the Matrix conflict check
   *  on the Inspector channel selector. */
  channel: number;
  /** Placement of the MIN/MAJ/Chrom scale selector. Default 'bottom'. */
  scalePlacement: WavesAutotuneScalePlacement;
  /** Show the tune-strength fader (CC 6). Absent = false.
   *
   *  OPT-IN rather than on by default, deliberately: this item already
   *  exists on the operator's authored pages, and defaulting it to true
   *  would silently re-flow every one of them - the fader takes a row, so
   *  the keys shrink. A gig layout should not change shape because an app
   *  update added a control. Turn it on per item in the Inspector. */
  showStrength?: boolean;
  /** Show-mode guard (SD-5): first tap on a key or scale ARMS it (2s), the
   *  second tap selects + sends the CC. Absent = false. */
  guard?: boolean;
  color: LayoutColor;
  /** Which mesh OPBridge(s) this row's CC output fans out to. See
   *  ButtonItem.bridgeIds for the full contract. */
  bridgeIds?: string[] | undefined;
}

/**
 * MixerItem - a composite channel-strip bank that mirrors + drives Ableton's
 * mixer through the OP-AbletonMixer M4L hub (DEC-M1, MIXER-PAGE-DESIGN.md).
 * Like ButtonGrid / WavesAutotune, it owns an internal multi-cell layout (N
 * vertical strips) rather than being one control. v0.1 = one 8-track bank.
 *
 * Control (iPad -> Live) dispatches OSC via OPBridge `POST /osc/send` to the
 * device's udpreceive on `oscTarget` (default 127.0.0.1:11230) - same spine as
 * TransposeItem. Feedback (Live -> iPad) arrives later via an OPBridge mixer
 * feed; live per-strip state lives in `mixer/session.ts` (keyed by absolute
 * track index), NOT on this item - the item is config only, so it never goes
 * stale against Live.
 *
 * Total width derives from `trackCount x cellW` (per-strip sizing, like
 * buttongrid); resizes via the S/M/L preset only (no freeform - the strips
 * would distort). Fader value = Live's normalized volume 0..1 with a unity
 * detent at 0.85 (DEC-M2); the dB is a display-only readout.
 */
/** Visual skin for the Mixer - the operator picks the look per item.
 *  `neon` = the app's native flat-glow look; `console` = modern dark studio
 *  hardware (molded caps, milled channel); `neve` = warm vintage Neve desk;
 *  `ableton` = Live's flat dark session-mixer look (thin fader + dB scale,
 *  blue solo, red arm circle, colored track chip). */
export type MixerSkin = 'neon' | 'console' | 'neve' | 'ableton';

export interface MixerItem extends FaceOptions {
  id: string;
  kind: 'mixer';
  x: number; y: number; w: number; h: number;
  size?: SizePreset;
  /** Strips shown. v0.1 fixed at 8 (the device BANK_SIZE). */
  trackCount: number;
  /** Absolute track index of strip 0 (bank paging). v0.1 = 0. */
  bankOffset: number;
  /** Render a mute button per strip. Default true. */
  showMute: boolean;
  /** Render a meter bar per strip (only shows data while the device METERS
   *  toggle is on - it defaults off for CPU). Default false. */
  showMeters: boolean;
  /** Render the track-name label per strip. Default true. */
  showNames: boolean;
  // ── v0.2 stub controls (solo / arm / pan / sends). All default OFF/absent so
  //    an existing volume+mute bank renders unchanged. The device (OP-AbletonMixer
  //    v0.1) doesn't act on these verbs yet — they go two-way when device v0.2 +
  //    OPBridge feedback land (see OPApps/opbuttons-opm4lbuilder.md). Config here;
  //    live per-strip solo/arm/pan/send values live in mixer/session.ts.
  /** Render a solo button per strip. Default false (stub). */
  showSolo?: boolean;
  /** Render a record-arm button per strip. Default false (stub). */
  showArm?: boolean;
  /** Render a pan slider per strip. Default false (stub). */
  showPan?: boolean;
  /** Render per-send sliders per strip. Default false (stub). */
  showSends?: boolean;
  /** How many send slots to render when `showSends`. Default 2. */
  sendCount?: number;
  /** Master-track mode: render a SINGLE strip mirroring Live's master track
   *  (`live_set.master_track`) instead of the regular-track bank. Master has
   *  volume + pan only — no mute/solo/arm/sends (Live doesn't expose them). The
   *  strip keys on the reserved index -1 and dispatches the `set_master_*` verbs.
   *  Default false. Mutually exclusive with `returns`. */
  master?: boolean;
  /** Return-tracks mode: render a bank of return strips (`live_set.return_tracks`)
   *  instead of regular tracks. Returns have volume/pan/mute/solo/sends — no arm.
   *  Strips key on `RETURN_BASE + i` and dispatch the `set_return_*` verbs.
   *  Default false. Mutually exclusive with `master`. */
  returns?: boolean;
  /** How many return strips to render in `returns` mode. Default 2 (A/B, the
   *  common Ableton default). */
  returnCount?: number;
  /** UDP host:port of the device's udpreceive (control-out target).
   *  Default 127.0.0.1:11230 (`MIXER_OSC_TARGET`). */
  oscTarget?: string;
  /** Show-mode guard: long-press-to-grab a strip fader before it responds
   *  (DEC-072), same as FaderItem. */
  guard?: boolean;
  /** Visual skin (fader / channel / mute rendering). Absent = 'console'. */
  skin?: MixerSkin;
  color: LayoutColor;
  /** Which mesh OPBridge(s) this strip bank's OSC output fans out to. See
   *  ButtonItem.bridgeIds for the full contract. */
  bridgeIds?: string[] | undefined;
}

/**
 * RadialItem (DEC-116) — a rotary knob sending one absolute CC.
 *
 * Earns its own kind rather than being a re-skinned FaderItem: a knob is
 * SQUARE and compact, and that is the whole reason authors reach for one
 * when vertical space is scarce. Folding it into a fader would silently
 * change the shape of an imported TouchOSC layout, which DEC-113 forbids.
 * Spec read off the corpus (`docs/iPad2 8.tosc`): both RADIALs are
 * 150x150 carrying a single absolute CC.
 *
 * Gesture is VERTICAL-DRAG, not circular-track: it matches every DAW's
 * knob convention, works on a small target, and avoids the atan2 pointer
 * math `RotationDial` needed. Drag up increases.
 *
 * Deliberately NOT in v1 (DEC-116): endless/relative encoder mode (zero
 * instances across the corpus), detents, bipolar centre-return (the
 * mixer's pan slider already covers bipolar).
 */
export interface RadialItem extends FaceOptions {
  id: string;
  kind: 'radial';
  x: number; y: number; w: number; h: number;
  size?: SizePreset;
  ccNumber: number;
  /** MIDI channel 0-15 (0-based, like FaderItem — see the channel-base
   *  note in TOSC-IMPORT-DESIGN.md before importing into this). */
  channel: number;
  /** Last-sent value (0-127). Persisted so the knob restores its angle. */
  value: number;
  color: LayoutColor;
  /** Value a double-tap/double-click snaps back to (DEC-110's gesture,
   *  reused). Default 0. */
  defaultValue?: number;
  /** Show-mode guard (SD-5 / DEC-072): long-press to grab before the knob
   *  responds; a quick brush sends nothing. Absent = false. */
  guard?: boolean;
  /** Sweep of the visual arc in degrees. Purely cosmetic - the value is
   *  always 0..127 across the full sweep. Default 270 (DAW convention). */
  sweepDeg?: number;
  /** Number of DISCRETE detent positions across the sweep. Absent (or 0) =
   *  continuous 0..127. Minimum 1. With N >= 2 the knob snaps to N evenly
   *  spaced values and draws N ticks, which is how you get a 3-way selector
   *  or a 5-position filter switch out of the same item. */
  steps?: number;
  destinations?: PortId[];
  /** Which mesh OPBridge(s) this knob's CC output fans out to. See
   *  ButtonItem.bridgeIds for the full contract. */
  bridgeIds?: string[] | undefined;
}

/**
 * Which device profile a PresetChangeItem drives. ONE item kind carries all
 * profiles (DEC-148); adding a Kemper or Axe-FX later is a new profile plus
 * a palette tile, never a new kind or a new dispatcher path.
 */
export type PresetChangeDevice = 'helix';

/**
 * Configured guitar-effects pedals for this project — the "Guitar FX
 * Pedals" list in Settings. Project-scoped (not a per-browser Settings
 * preference) because a pedal is rig truth, same reasoning as DEC-076:
 * it should travel with the project and mirror to the OPBridge doc store
 * like everything else project-relevant, not reset per-device.
 *
 * Two shapes, deliberately not one:
 *   - `'helix'` — a real `PresetChangeItem`/`SnapshotChangeItem` profile
 *     (DEC-148/151). Port is AUTO-DETECTED by searching `GET /midi/ports`
 *     for a name containing "Helix" — the operator never types a port for
 *     this type. Channel defaults to 15 (displayed 16), the operator's
 *     confirmed base channel, not the usual channel-1 assumption.
 *   - `'custom'` — NOT a new PresetChangeItem profile. A custom pedal has
 *     no proprietary USB protocol to read presets from (only the Helix
 *     does), so it reuses the ALREADY-SHIPPED Button + `midi.pc`/`midi.cc`
 *     action (CP-075) — port/channel pre-filled from this entry, and
 *     `presetNames` supplies the "pick by name" list the Helix gets from a
 *     USB read but a custom pedal can only get from the operator typing it
 *     once, same idea as `helixSetlistOverride` in `helix/library.ts`.
 *     Port is a manual pick too, but **only for the FIRST `'custom'` pedal
 *     ever registered on a project** (DEC-152 amended 2026-08-03) — every
 *     `'custom'` pedal after that inherits the first one's port
 *     automatically, since sharing one hardware MIDI interface across
 *     several pedals on different channels is the common rig shape and a
 *     Custom pedal has no name to auto-detect by (unlike Helix). Never
 *     inherits a `'helix'` pedal's port — that's a device-specific USB
 *     port only the Helix itself answers on.
 */
export type GuitarPedalType = 'helix' | 'custom';

/**
 * Catalog entry for the "Connected Devices" add-a-pedal flow (data-driven,
 * 2026-08-04). Adding a model that behaves like an existing `GuitarPedalType`
 * (a simple PC/CC pedal with no proprietary protocol) needs only a new row
 * here. A model with its OWN multi-message compound protocol (the next
 * Helix-like device) still needs a new `PresetChangeDevice` value plus a
 * dispatcher case (DEC-148/152's sub-decision 4) — this catalog removes the
 * UI-side friction of adding a model, not the wire-protocol work a genuinely
 * new protocol requires.
 */
export interface PedalModel {
  id: string;
  brand: string;
  model: string;
  type: GuitarPedalType;
  /** Substring (case-insensitive) to auto-detect this model's port by name
   *  from `GET /midi/ports`. Absent = no auto-detect; the port is inherited
   *  from an existing pedal of the same type or picked once, manually. */
  autoDetectSubstring?: string;
  /** 0-based default channel a freshly-added pedal of this model starts on.
   *  Absent = 0 (channel 1). */
  defaultChannel?: number;
}

/** The two devices this project actually knows how to talk to today.
 *  New rows go here, not as new buttons in a component — see the doc
 *  comment above. */
export const PEDAL_MODELS: PedalModel[] = [
  {
    id: 'line6-helix-floor',
    brand: 'Line 6',
    model: 'Helix Floor',
    type: 'helix',
    autoDetectSubstring: 'helix',
    // The operator's confirmed base channel, not the usual channel-1
    // assumption (cost real debugging time before it was caught, CP-075).
    defaultChannel: 15,
  },
  {
    id: 'generic',
    brand: '',
    model: 'Generic pedal',
    type: 'custom',
  },
];

export function pedalModel(modelId: string): PedalModel | undefined {
  return PEDAL_MODELS.find((m) => m.id === modelId);
}

export interface GuitarPedal {
  id: string;
  /** Operator-given name (e.g. "Boss GT-1000"). For `'helix'` this is
   *  editable but seeded from the auto-detected port's device name. */
  name: string;
  /** Which catalog row this pedal was added from (drives the add-flow's
   *  display and default channel). Behavior (auto-detect vs manual port,
   *  compound protocol vs plain PC/CC) is still driven by `type`, which a
   *  model's catalog entry seeds at creation and never changes afterward. */
  modelId: string;
  type: GuitarPedalType;
  /** Named MIDI output. Absent = not found/not assigned yet, which is a
   *  loud-dead-state per DEC-138/124, same as an unset PresetChangeItem
   *  port — never silently falls back to the rig default (Ableton).
   *  Explicitly `| undefined` for `exactOptionalPropertyTypes`. */
  port?: string | undefined;
  /** Which mesh OPBridge this device is physically wired to (e.g. the
   *  Helix's USB connection lands on a specific machine, not every bridge
   *  — DEC-147's motivating case). Absent = the default bridge (today's
   *  `is_primary` row), preserving pre-affinity behavior until the
   *  operator explicitly assigns one here. Resolved LIVE by every item
   *  bound via `pedalId`, same as `port`/`channel` — never copied onto the
   *  item. Explicitly `| undefined` for `exactOptionalPropertyTypes`. */
  bridgeId?: string | undefined;
  /** The bridge's STABLE `nodeId`, and the authoritative binding once set.
   *
   *  ⚠️ `bridgeId` above is a DEVICE-LOCAL row id (`newBridgeId()` is
   *  `Math.random()` plus a timestamp) and pedals ride the synced project
   *  document, so that id was replicating mesh-wide and resolving to nothing
   *  on every machine but the one that minted it. `nodeId` is the join key
   *  DEC-465 already designates for exactly this reason.
   *
   *  Resolve with `pedals/bridgeBinding.ts::pedalBridgeRowId`, never by
   *  reading this directly - an unresolvable node must degrade to "not
   *  connected" and must NOT collapse to the default bridge. `bridgeId` is
   *  kept as the pre-migration fallback and is ignored whenever this is set. */
  bridgeNodeId?: string | undefined;
  /** MIDI channel 0-15 (0-based, matching PresetChangeItem/SnapshotChangeItem
   *  and the majority item convention). The ONLY place this is authored —
   *  every item bound to this pedal (via `pedalId`) resolves its channel
   *  from here live, and the item Inspector never offers its own channel
   *  field for a pedal-bound item (2026-08-04 operator ask: "keep the
   *  option non-changeable in the item inspector"). */
  channel: number;
  /** `'custom'` only: PC/CC value -> operator-typed preset name. Absent for
   *  `'helix'`, whose names come from `helix/library.ts`'s USB-read cache
   *  instead. Explicitly `| undefined` for `exactOptionalPropertyTypes`. */
  presetNames?: Record<number, string> | undefined;
}

/**
 * PresetChangeItem (DEC-148) — recall a preset on an external device.
 *
 * Its own kind rather than a Button action type because ONE preset change is
 * TWO ordered MIDI messages (setlist CC then preset PC), which a
 * single-message Button cannot express without a Macro — exactly the "spend
 * time figuring it out" this is designed away from.
 *
 * ## Helix wire format (official 3.80 manual, verified on hardware 2026-08-01)
 *
 *   CC32 = setlist (0-7)  →  PC = preset (0-127)  →  CC69 = snapshot (0-7)
 *
 * Ordered, and **no inter-message delay is required**: the manual states
 * CC69 received during a preset load is buffered and applied once the load
 * finishes. The setlist CC is sent on EVERY fire, not only on change — the
 * device's setlist is sticky and the player may have moved it by hand, so a
 * bare PC would resolve against the wrong setlist. Deterministic beats
 * minimal here.
 *
 * ## Channel is 0-BASED on this item
 *
 * Stored 0-15, displayed 1-16 — matching Button/Fader/Radial, i.e. the
 * majority convention. (Waves-autotune and the Matrix are 1-based; that
 * inconsistency is a known trap, so this picks the dominant side and says so
 * rather than adding a third variant.)
 */
export interface PresetChangeItem extends FaceOptions {
  id: string;
  kind: 'presetChange';
  x: number; y: number; w: number; h: number;
  size?: SizePreset;
  /** Drives the vocabulary (Setlist/Preset/Snapshot) and the wire format. */
  device: PresetChangeDevice;
  /** Which registered `GuitarPedal` this tile fires on (Connected Devices,
   *  2026-08-04). Port and channel are resolved LIVE from that pedal, never
   *  copied — so re-pointing or re-channeling a device in Connected Devices
   *  retunes every tile bound to it with no per-item re-authoring. Absent,
   *  or pointing at a pedal that no longer exists, is a loud dead state
   *  (DEC-124/138) exactly like the old unset-port state was — the item
   *  never falls back to the rig default (Ableton) and never fires on a
   *  stale port/channel of its own. Superseded `port`/`channel` fields on
   *  this item (removed 2026-08-04 amendment to DEC-152 — see DECISIONS.md). */
  pedalId?: string | undefined;
  /** DEC-154 inbound MIDI trigger - A-path (2026-08-28). Resolves through the
   *  SAME `timeline/bridgeSync.ts::resolveFireableItemActions` a Timeline cue
   *  uses, so a pedal press and a timeline step fire byte-identical messages.
   *  ON side only: a preset change is a state change on the pedal, not a held
   *  gesture, so there is no release edge to bind. */
  midiTrigger?: MidiTriggerBinding | undefined;
  /** Setlist 0-7 → CC32. */
  setlist: number;
  /** Preset 0-127 → Program Change. */
  program: number;
  /** Snapshot 0-7 → CC69. Absent = do not touch the snapshot, which is the
   *  right default: recalling a preset should not silently move a snapshot
   *  the player set by hand. */
  snapshot?: number | undefined;
  /** Display name from a probed/imported library, cached on the item so the
   *  face keeps reading correctly when the bridge is offline. Absent = show
   *  the numbers. */
  presetName?: string | undefined;
  /** Show-mode guard (SD-5 / DEC-072): first tap arms, second fires. */
  guard?: boolean;
  color: LayoutColor;
  destinations?: PortId[];
}

/**
 * Which snapshot a SnapshotChangeItem selects. `0-7` are the eight absolute
 * slots; `'next'` and `'prev'` are the device's own relative moves (CC69
 * values 8 and 9 on a Helix), which are what a per-section "advance" button
 * needs and which no absolute value can express.
 */
export type SnapshotTarget = number | 'next' | 'prev';

/**
 * SnapshotChangeItem (DEC-151) — select a snapshot on an external device
 * WITHOUT recalling a preset.
 *
 * ## Why this is not a mode on PresetChangeItem
 *
 * A preset and a snapshot are different concepts on the device, not two
 * settings of one concept — DEC-148 already recorded this ("snapshots are a
 * third concept again"). A preset is a rig; a snapshot is a state WITHIN
 * that rig, and moving between snapshots is seamless where moving between
 * presets is a load. Folding them together would have produced a tile
 * called "preset change" whose job was to not change the preset.
 *
 * The practical half: before this item the ONLY route to CC69 was the
 * preset tile's optional ride-along, which always sends CC32 + PC first.
 * That makes every snapshot move a preset reload — precisely the thing
 * snapshots exist to avoid.
 *
 * ## Helix wire format (official 3.80 manual)
 *
 *   CC69 = 0-7 → snapshot 1-8;  8 → next;  9 → previous
 *
 * ONE message, on the device's own port and base channel. No setlist CC and
 * no Program Change — sending either would reload the preset.
 *
 * Channel is 0-BASED here, matching PresetChangeItem and the majority
 * convention (Button/Fader/Radial). Stored 0-15, displayed 1-16.
 */
export interface SnapshotChangeItem extends FaceOptions {
  id: string;
  kind: 'snapshotChange';
  x: number; y: number; w: number; h: number;
  size?: SizePreset;
  /** Drives the vocabulary and the wire format. Shares PresetChangeItem's
   *  profile type — a Kemper/Axe-FX profile lands on both or neither. */
  device: PresetChangeDevice;
  /** Which registered `GuitarPedal` this tile fires on — same live-resolve
   *  contract as `PresetChangeItem.pedalId` (see its doc comment). Absent,
   *  or a removed pedal, is a loud dead state, never a silent fall-back to
   *  the rig default. */
  pedalId?: string | undefined;
  /** DEC-154 inbound MIDI trigger - A-path (2026-08-28). Same shared resolver
   *  as PresetChange above; ON side only, no release edge. A `next`/`prev`
   *  snapshot binds fine - it resolves to CC69 value 8/9, so a pedal can step
   *  through snapshots without knowing which one is current. */
  midiTrigger?: MidiTriggerBinding | undefined;
  /** Which snapshot to select. Absolute 0-7, or a relative move. */
  snapshot: SnapshotTarget;
  /** Show-mode guard (SD-5 / DEC-072): first tap arms, second fires. */
  guard?: boolean;
  color: LayoutColor;
  destinations?: PortId[];
}

/**
 * LabelItem (DEC-117) — standalone display text. Fires NOTHING.
 *
 * Claims no CC, so it is invisible to the SD-14 dup-CC linter, takes no
 * part in guard / performance-mode gesture handling, and never appears in the
 * Matrix. It is a pure display primitive.
 *
 * Why it earns a kind: LABEL+TEXT nodes are 50-57% of every file in the
 * TouchOSC corpus, of which 26-49 per file are standalone headings rather
 * than control labels absorbed by `associateLabels`. Before this, the only
 * way to put text on a page was a ButtonItem that does not fire — dragging
 * along button chrome, a hit target, shape + rotation fields, guard
 * semantics and linter noise. The importer exposed the gap; it did not
 * create it.
 *
 * Also absorbs the BOX/divider case (thin decorative frames) via the
 * optional background + border, so BOX does not need its own kind.
 */
/* Extends FaceOptions for consistency with every other kind (ImageItem and
 * MixerItem do the same despite having no conventional face). `name` is the
 * item's Inspector identity, distinct from the `text` it displays. The
 * `hideName` / `hideLabel` / `nameSize` flags are inert here - a Label has
 * no face lines to hide - but keeping the shape uniform means the shared
 * rename + face-flag paths in store.ts and InspectorPanel.tsx need no
 * per-kind narrowing. */
export interface LabelItem extends FaceOptions {
  id: string;
  kind: 'label';
  x: number; y: number; w: number; h: number;
  size?: SizePreset;
  /** The text. Multi-line: authored in a textarea per DEC-052, rendered
   *  with `white-space: pre-line`. */
  text: string;
  color: LayoutColor;
  /** Horizontal alignment. Default 'center'. */
  align?: 'left' | 'center' | 'right';
  /** Vertical alignment. Default 'middle'. */
  valign?: 'top' | 'middle' | 'bottom';
  /** Font sizing strategy — reuses the NameSize vocabulary so the
   *  per-line auto-shrink helper (DEC-053) applies unchanged. */
  textSize?: NameSize;
  /** Draw a filled background behind the text. Default false (text floats
   *  on the canvas). Turning this on with a thin frame is the BOX case. */
  background?: boolean;
  /** Draw a border. Default false. */
  border?: boolean;
}

/**
 * RigStateButtonItem (PLAN.md QUEUED "Rig States (visual recall)") - recall
 * ONE named look from a Rig State bank. Button-shaped, placeable on any
 * page, same footing as FireButton - the "instant chord" gap between
 * FireButton (1 action) and Timeline (sequenced).
 *
 * Deliberately fires exactly one look, not a bank-wide picker on the face:
 * layering / radio-switching a bank happens by placing several of these
 * (one per look), matching the locked semantics' "layering across a song =
 * firing multiple banks" - there is no on-face bank switcher to keep the
 * release-only-arbitration model honest (no priority ladder hiding in the
 * UI). `bankId` alone (no `lookId` yet) is a real interim state - the
 * author picked a bank and hasn't picked a look - not the same as "no bank
 * assigned"; both render the same loud dead state (DEC-124/138 convention)
 * since neither can fire.
 */
export interface RigStateButtonItem extends FaceOptions {
  id: string;
  kind: 'rigStateButton';
  x: number; y: number; w: number; h: number;
  size?: SizePreset;
  bankId?: string;
  lookId?: string;
  /** Show-mode guard (SD-5 / DEC-072): first tap arms, second fires. */
  guard?: boolean;
  color: LayoutColor;
}

/**
 * LIGHTING ITEMS (PLAN.md QUEUED "DMX / lighting v1", DEC-013).
 *
 * Four kinds, and the shape of the set is the decision. DEC-013 called v1 "a
 * fixture wizard + manual control page"; OPButtons has no page TYPES, so the
 * "manual control page" is a page the operator filled with lighting items.
 * That means lighting arrives as item kinds on the canvas, alongside Button
 * and Fader, rather than as a console bolted onto the side. A console inside
 * one tile would put a second navigation layer in front of every action,
 * which is the wrong trade on a device whose whole point is muscle memory.
 *
 * All four carry a `DmxTarget` rather than a fixture id, so "all lights",
 * "the wash group", "Back Left" and "Block 3 of the COLORband" are the same
 * amount of work to author. None of them can express a universe or a DMX
 * address - see `DmxTarget` for why that is a lock and not an omission.
 *
 * The one that does NOT carry a target is Blackout, deliberately: it is the
 * lighting panic path and a kill switch that can be aimed is a kill switch
 * that can be aimed wrong.
 */

/** Set a colour (and optionally a level) on the target. The hero of the set:
 *  "I want the back wash deep blue" is one tap. Colour comes from DEC-058's
 *  sixteen stage swatches, stored by LABEL rather than by RGB so a future
 *  palette correction reaches already-placed buttons - the swatch is the
 *  operator's vocabulary, the RGB is an implementation of it. */
export interface LightColorItem extends FaceOptions {
  id: string;
  kind: 'lightColor';
  x: number; y: number; w: number; h: number;
  size?: SizePreset;
  target: DmxTarget;
  /** Label from `lighting/palette.ts`'s STAGE_COLORS. */
  color_label: string;
  /** Optional master level 0-255 to set alongside the colour. Omitted means
   *  "leave the level alone", which is the `/state` route's own semantics and
   *  the reason a colour button does not silently full-brighten a rig the
   *  operator had dimmed. */
  level?: number;
  /** Show-mode guard (SD-5 / DEC-072): first tap arms, second fires. */
  guard?: boolean;
  color: LayoutColor;
}

/** A fader bound to the target's master level. Sends `/state` with `level`
 *  only, so moving it cannot disturb a colour. */
export interface LightLevelItem extends FaceOptions {
  id: string;
  kind: 'lightLevel';
  x: number; y: number; w: number; h: number;
  size?: SizePreset;
  target: DmxTarget;
  /** Last authored/moved position, 0-255. Persisted so the fader renders
   *  where the operator left it rather than snapping to zero on reload.
   *  NOT a claim about the rig - see the component for the honesty rule. */
  value: number;
  /** Orientation, matching FaderItem. `false` lays it out horizontally;
   *  absent means vertical, which is what a level fader is. */
  vertical?: boolean;
  /** Show-mode guard (SD-5 / DEC-072). On a fader this is the press-and-hold
   *  GRAB, not an arm-then-fire - a brush sends nothing, a deliberate hold
   *  takes the handle. Same mechanism FaderItem uses. */
  guard?: boolean;
  color: LayoutColor;
}

/** Arm a named effect on the target. Two sources of effect names, kept
 *  visually separate because only one of them follows the clock: OPBridge's
 *  software effects (`/dmx/fx-catalog`, of which `beat-strobe` / `bar-pulse`
 *  / `beat-cycle` are musical) and the fixture's own built-in FX macros
 *  (from its profile mode, e.g. the PL60C's seventeen). */
export interface LightFxItem extends FaceOptions {
  id: string;
  kind: 'lightFx';
  x: number; y: number; w: number; h: number;
  size?: SizePreset;
  target: DmxTarget;
  /** Effect slug. Empty string is meaningful: OPBridge reads it as STOP. */
  effect: string;
  /** 0-255, default full. */
  brightness?: number;
  /** Base colour label for the effects that tint the operator's chosen hue
   *  (strobe / pulse / bar-pulse). Ignored by the ones that define their own
   *  (fire / lightning / rainbow / beat-cycle) - and the Inspector says so
   *  rather than offering a control that does nothing. */
  color_label?: string;
  guard?: boolean;
  color: LayoutColor;
}

/** Kill the rig. `POST /dmx/blackout`, no target, every reachable bridge. */
export interface BlackoutItem extends FaceOptions {
  id: string;
  kind: 'blackout';
  x: number; y: number; w: number; h: number;
  size?: SizePreset;
  /** Defaults ON for this kind alone. Every other guard defaults off; a
   *  rig-wide kill is exactly the control worth one extra tap in show mode,
   *  and the operator who needs it in a hurry is also the operator most
   *  likely to brush it. */
  guard?: boolean;
  color: LayoutColor;
}

// ============================================================
// OPVisual (2026-09-24, .notepads/opvisual-control-plan.md)
// ============================================================

/**
 * One OPVisual action on a button: fire a cell or a column, drive the chain,
 * clear a layer, STOP ALL, blackout, panic, reload. A separate kind from
 * Button by the operator's choice ("separate for the time being").
 *
 * Sends through the bridge on the OPVisual machine (see `sendOpVisual`) -
 * OPBridge protocol by default, OSC if `sendVia` says so (DEC-215) - so
 * `bridgeIds` names exactly ONE machine and an unaimed item is refused.
 */
export interface OpVisualTriggerItem extends FaceOptions {
  id: string;
  kind: 'opvisualTrigger';
  x: number; y: number; w: number; h: number;
  size?: SizePreset;
  action: OpVisualAction;
  /** When a cell / column / chain-goto lands. `bar` is the default and the
   *  operator's normal case; ignored by verbs that are instant by nature. */
  quant: OpvQuant;
  /** Exactly one machine - the one running OPVisual. */
  bridgeIds?: string[] | undefined;
  /** The channel ("Sends via", DEC-215). Absent = `'opbridge'`, so every item
   *  saved before 2026-09-28 moves to OPBridge protocol with no migration;
   *  `'osc'` is the user's explicit choice of the old path. */
  sendVia?: OpvVia | undefined;
  /** Show-mode guard (SD-5 / DEC-072): first tap arms, second fires. */
  guard?: boolean;
  color: LayoutColor;
}

/** How a pad plays (OPBridge `/pads/play` `mode`): once to the end, loop until pressed again, or while held. */
export type AudioPadMode = 'oneshot' | 'loop' | 'gate';
/** When a pad starts: at once, on the next beat, or on the next bar of Ableton's clock. */
export type AudioPadQuant = 'now' | 'beat' | 'bar';
/** Off = the file's own speed; repitch = sped up or slowed to Ableton's tempo (pitch moves with it). */
export type AudioPadWarp = 'off' | 'repitch';

/** One pad: a file from the audio library (Navigate > Audio) and how it plays. Every field but the file is optional;
 *  an absent one takes the file's TYPE default (`audio/pads.ts` `padDefaults`), so a pad follows its file when the
 *  operator re-types it in the library. */
export interface AudioPad {
  trackId?: string;
  label?: string;
  mode?: AudioPadMode;
  quant?: AudioPadQuant;
  warp?: AudioPadWarp;
  /** 0..2, 1 = as recorded. */
  gain?: number;
  color?: CellColor;
}

/**
 * Audio pads (2026-10-13, operator: "an audio file top level item... a soundboard... 1 button or a grid... launch on
 * bar or now... like ableton's drum rack"). Plays library files on a PC's output during the show through OPBridge's
 * `/pads/*` routes (capability `audio_pads`), several at once. One item covers the single pad (1 x 1) and the grid.
 * Holds CONFIG only; what is sounding is OPBridge's (`GET /pads/status`, polled by `audio/pads.ts`).
 */
export interface AudioPadsItem extends FaceOptions {
  id: string;
  kind: 'audioPads';
  x: number; y: number; w: number; h: number;
  size?: SizePreset;
  rows: number;
  cols: number;
  /** rows * cols entries, row by row. */
  pads: AudioPad[];
  /** The PC whose output plays (an OPBridge node id). Absent = the primary bridge's own PC. */
  pc?: string | undefined;
  /** That PC's output by its name. Absent = its default output. */
  output?: string | undefined;
  /** Show-mode guard (SD-5 / DEC-072): first tap arms, second fires. */
  guard?: boolean;
  color: LayoutColor;
}

/**
 * OPVisual's clip grid, mirrored: column headers fire columns, cells fire
 * cells, each layer row carries on/off, opacity, blend, speed and clear, and
 * a top bar carries launch moment, health, SHAKE, STOP ALL, blackout and the
 * screen key (no chain or PANIC since DEC-272), and 16:9 cells.
 * Drop it full-canvas on its own page to get "the OPVisual page" - the same
 * item-not-page-type choice DEC-100 made for the Mixer.
 *
 * Holds CONFIG only. What was sent lives in `opvisual/session.ts`; what
 * OPVisual reports (on air, health, levels) in `opvisual/live.ts` (DEC-272).
 */
export interface OpVisualGridItem extends FaceOptions {
  id: string;
  kind: 'opvisualGrid';
  x: number; y: number; w: number; h: number;
  size?: SizePreset;
  quant: OpvQuant;
  /** Columns shown, from column 1. 1..8; 8 is the whole grid. */
  cols: number;
  /** Per-layer on/off, opacity, blend, speed, clear in each row's gutter. */
  showLayerControls: boolean;
  /** Master opacity and the look sliders (brightness, hue, sat, bloom). */
  showMasterLook: boolean;
  /** The visualiser strip: six knobs for one layer's module visualiser and
   *  NEW TAKE (DEC-272). Absent = shown, so grids saved before it get it. */
  showViz?: boolean;
  /** The program monitor: OPVisual's live output, polled from OPBridge's
   *  `/opvisual/preview.jpg` while this grid is on screen (OPBridge S314).
   *  Absent = off: opt-in, so grids saved before it keep their layout and
   *  nothing polls OPVisual unless asked. */
  showMonitor?: boolean;
  /** Each layer's own colour (2026-10-18; glass-items-study gave every layer
   *  one). Index = layer - 1. Absent, `null` or `'none'` = the item's colour,
   *  so grids saved before it are unchanged. Lights that layer's loaded and
   *  on-air cells like any colour-as-light (DEC-271). */
  layerHues?: Array<PaletteHue | null> | undefined;
  /** Follow (2026-10-18, OPVisual DEC-148): absent / true = a fired cell runs
   *  its own "then" (go right, down...); false = it counts its plays and ends
   *  without following (`follow: false` on the trigger). */
  follow?: boolean | undefined;
  /** Exactly one machine - the one running OPVisual. */
  bridgeIds?: string[] | undefined;
  /** The channel ("Sends via", DEC-215). Absent = `'opbridge'`, so every item
   *  saved before 2026-09-28 moves to OPBridge protocol with no migration;
   *  `'osc'` is the user's explicit choice of the old path. */
  sendVia?: OpvVia | undefined;
  color: LayoutColor;
  /** DEC-320: STOP ALL, B/OUT and CLR need a second tap. Cells and columns never ask. Absent = off. */
  guard?: boolean;
}

/**
 * SynesthesiaGridItem (2026-11-05, OPBridge S344/S352; `.notepads/synesthesia-app-half.md`) - Synesthesia, the VJ app on
 * STREAMPC, as a page: its scenes as pictures (a tap launches one, landing on the bar), the moves (previous, random,
 * next, home), Synesthesia's own playlist steps, the look (its meta controls as sliders and switches, read from
 * OPBridge's list) and the playing scene's own controls. Sent through any connected computer: OPBridge finds the PC that
 * drives Synesthesia, so there is no machine to pick. One-way MIDI: nothing is drawn ON AIR, only WAITING and SENT.
 */
export interface SynesthesiaGridItem extends FaceOptions {
  id: string;
  kind: 'synesthesiaGrid';
  x: number; y: number; w: number; h: number;
  size?: SizePreset;
  /** When a scene or move lands: the next bar (absent, the default), the next beat, or now. Sliders always go now. */
  when?: SynWhen | undefined;
  /** Which scenes: every scene OPBridge lists (absent) or the operator's own scene folder only. */
  scenes?: 'all' | 'own' | undefined;
  /** Columns of scene pictures; absent = as many as fit at a readable size. */
  cols?: number | undefined;
  /** Scene names under the pictures. Absent = shown. */
  sceneNames?: boolean | undefined;
  /** Previous / Random / Next / Home. Absent = shown. */
  showMoves?: boolean | undefined;
  /** The look: Synesthesia's meta controls (hue, saturation, brightness...). Absent = shown. */
  showLook?: boolean | undefined;
  /** Synesthesia's own playlist: previous, timer, next. Absent = hidden. */
  showPlaylist?: boolean | undefined;
  /** The playing scene's own controls 1-8 (each taught once in Synesthesia). Absent = hidden. */
  showSceneControls?: boolean | undefined;
  color: LayoutColor;
  /** Home needs a second tap (it leaves the show for the home playlist). Absent = off. */
  guard?: boolean;
}

/**
 * "Share with mods" on ANY item (operator, 2026-09-29: *"anything including
 * run a program should be shareable with mods"*). Joined onto every member
 * of `LayoutItem` here rather than declared on ~30 interfaces. Makes the item
 * GRANTABLE, never granted - the per-mod grant is OPMod's. Absent = false.
 * What a share registers per kind is `modShare/ops.ts`.
 */
export interface ModShareable {
  shareWithMods?: boolean;
  /** A controller in OPBridge is assigned to this item (DEC-256): keep its
   *  named actions registered, since that is what OPBridge fires on a
   *  press, whether or not it is shared with mods. Set by "Assign
   *  controller...", cleared when the last of its bindings is removed from
   *  here. Registered is still only GRANTABLE: OPMod lists it, and grants
   *  nothing on its own. Absent = false. */
  controllerBound?: boolean;
}

export type LayoutItem = ModShareable & (
  | ButtonItem
  | LfoItem
  | ButtonGridItem
  | SequencerItem
  | StreamDeckItem
  | MacroItem
  | FaderItem
  | XYPadItem
  | TimelineLauncherItem
  | PianoItem
  | ImageItem
  | TransposeItem
  | CapoItem
  | RecQuantItem
  | AbletonRowItem
  | SceneCopyItem
  | WavesAutotuneItem
  | MixerItem
  | StatusLedItem
  | RadialItem
  | LabelItem
  | PresetChangeItem
  | SnapshotChangeItem
  | DisplayItem
  | RigStateButtonItem
  | LightColorItem
  | LightLevelItem
  | LightFxItem
  | BlackoutItem
  | OpVisualTriggerItem
  | OpVisualGridItem
  | SynesthesiaGridItem
  | AudioPadsItem
);

/**
 * Page — a single screen of layout items within a project. Projects have
 * one or more pages; the active page drives what's rendered + edited. Each
 * page carries its own hue (cycled through PAGE_HUE_PALETTE on add) so
 * the user can tell pages apart at a glance in the pills row.
 */
export interface Page {
  id: string;
  name: string;
  items: LayoutItem[];
  color: PageHue;
  /** RESERVED — schema only. Universal Trigger System will fire page
   *  switches via this field. v1 ignores it at runtime. */
  triggers?: Trigger[];
}

export interface Project {
  id: string;
  name: string;
  device: DeviceId;
  orientation: Orientation;
  /** EXACT authored frame in CSS points, when this project has been fitted to
   *  a real screen. Wins over the (device, orientation) preset - read it via
   *  `canvasSizeOf()`, never directly.
   *
   *  Exists because a preset is a guess and a measurement is not: the
   *  operator's iPad 8th gen hands a standalone PWA ~1068x800 of usable box
   *  against a nominal 1080x810, so ANY preset leaves a gap the shell cannot
   *  close. Additive and optional, same convention as `modifiedAt` and
   *  `pedals`: `projectSync` round-trips whole objects, so an older build
   *  carries it through rather than dropping it. */
  canvas?: { w: number; h: number };
  /** The strips the installed app on the device that PLAYS this project gives
   *  up to iOS (status bar on top, home indicator below), in CSS points, as
   *  MEASURED there. Wins over the device table - read it via `reserveOf()`.
   *
   *  Same principle as `canvas` (DEC-210): a guess is replaced by the
   *  device's own number. Exists because the table's 24pt top went stale
   *  within two weeks: iPadOS 26.6.1 reports a 32pt top inset, so the
   *  desktop's canvas was 8pt taller than the iPad's and a control dragged to
   *  the bottom on the desktop was cut off on stage (measured 2026-10-05,
   *  Navigate > Details frame report). Recorded by `maybeRecordReserve`,
   *  only from an installed app; additive and optional like `canvas`. */
  reserve?: { top: number; bottom: number };
  /** Always length ≥ 1. Deleting the last page is disallowed. */
  pages: Page[];
  /** Always references a valid id in pages[]. */
  activePageId: string;
  /** Stays at project level — CCs flow across pages so the rolling
   *  counter doesn't reset every time the user adds a page. */
  nextCC: number;
  createdAt: number;
  /** Last time this project's content changed on any device, ms epoch.
   *  The conflict-resolution key for cross-device sync: when two devices
   *  hold the same project id, the higher `modifiedAt` wins
   *  (`projectMerge.ts`). Stamped automatically by `update()` on every
   *  mutation, so no call site has to remember to set it.
   *
   *  Optional because projects already existed before sync needed this,
   *  and deliberately NOT a schema bump - purely additive, same
   *  convention as `pedals` / `panicMidiTrigger`. Always read it via
   *  `modifiedAtOf()`, which falls back to `createdAt`, never directly. */
  modifiedAt?: number | undefined;
  /** Configured guitar-effects pedals (Settings > Guitar FX Pedals).
   *  Absent on any project created before this shipped — always read via
   *  `?? []`, never assume presence. No version bump needed: purely
   *  additive and optional, same as `presetName`/`guard` on items. */
  pedals?: GuitarPedal[] | undefined;
  /** DEC-154 inbound MIDI trigger for the global Panic action (Settings >
   *  Panic MIDI Trigger) — the one binding with no canvas item to live on.
   *  Additive/optional, same convention as `pedals`. */
  panicMidiTrigger?: MidiTriggerBinding | undefined;
  /** Guitar drop tuning, -4..0, that every Capo row and the Display map the
   *  concert transpose through (`transpose/session.ts::dropTuning`). On the
   *  project since 2026-09-28 so it travels with sync: it used to be one
   *  browser's localStorage, so Eb set on the laptop left the iPad at 0 and a
   *  transpose -1 song lit capo -1, which is off the row - nothing lit.
   *  Absent = fall back to this device's old local value. Additive/optional. */
  dropTuning?: number | undefined;
  /** OP-Motion LANDING presets (2026-10-24, `MotionPreset`): what a Capture press stored, for a Landing preset
   *  button or a timeline to land. Additive/optional, same convention as `pedals`. */
  motionPresets?: MotionPreset[] | undefined;
}

/**
 * One OP-Motion landing preset (2026-10-24, OPM4LBuilder's ask). `buses` holds, per bus that answered the capture,
 * the reply's pairs (`/OP-Motion/state <bus> <id>` args 3 onward) VERBATIM: `tone <v> tone.motion <shape> ...`. The
 * reply IS a land message, so landing sends them as they are, and fields a later OP-Motion adds (v0.4) arrive and
 * travel without a change here. The capture leaves the crossfader out, so a preset never switches A / B by itself.
 */
export interface MotionPreset {
  id: string;
  /** The operator's name for it ("Verse"); a new capture is "Landing N". */
  name: string;
  buses: Record<string, Array<string | number>>;
  /** ms epoch of the last capture into it. */
  capturedAt: number;
}

interface ProjectsState {
  projects: Record<string, Project>;
  /** Insertion order, so the browser list has a stable sort. */
  order: string[];
}

// ─── v1 → v2 migration ───────────────────────────────────────────
// v1 stored Project { items, nextCC } with items inline at the project
// root. v2 wraps that into pages[0] and adds activePageId. The migrator
// is pure so the 6 spec test cases can verify it without spinning up
// localStorage. We keep PROJECTS_KEY_V1 readable for one release after
// v2 ships as a safety net.

/** Legacy v1 project shape — kept here so the migrator is self-contained. */
interface ProjectV1 {
  id: string;
  name: string;
  device: DeviceId;
  orientation: Orientation;
  items: LayoutItem[];
  nextCC: number;
  createdAt: number;
}

interface ProjectsStateV1 {
  projects: Record<string, ProjectV1>;
  order: string[];
}

function newPageId(): string {
  return `page-${Date.now().toString(36)}-${idSuffix()}`;
}

export function migrateV1toV2(v1: ProjectsStateV1): ProjectsState {
  const out: ProjectsState = { projects: {}, order: [...v1.order] };
  for (const [id, p] of Object.entries(v1.projects)) {
    const pageId = newPageId();
    out.projects[id] = {
      id: p.id,
      name: p.name,
      device: p.device,
      orientation: p.orientation,
      createdAt: p.createdAt,
      nextCC: p.nextCC,
      pages: [{
        id: pageId,
        name: 'Page 1',
        items: p.items,
        color: 'sky',
      }],
      activePageId: pageId,
    };
  }
  return out;
}

// ─── v2 → v3 migration ───────────────────────────────────────────
// v3 introduces multi-port destinations and new optional fields on
// existing item kinds (button mode/shape, action type, fader item,
// image item). The migration is purely additive: it walks every item
// and defaults the new fields when missing.
//
// We're shape-compatible — every v2 ProjectsState is a valid v3
// ProjectsState — but going through migrateV2toV3 makes the new fields
// explicit, which keeps round-tripping (export → import) deterministic.

export function migrateV2toV3(v2: ProjectsState): ProjectsState {
  // ProjectsState shape is identical; only item-level fields change. We
  // walk every page's items and ensure new optional fields have sensible
  // defaults so downstream code doesn't have to branch on undefined.
  const out: ProjectsState = { projects: {}, order: [...v2.order] };
  for (const [id, p] of Object.entries(v2.projects)) {
    out.projects[id] = {
      ...p,
      pages: p.pages.map((page) => ({
        ...page,
        items: page.items.map(defaultV3Fields),
      })),
    };
  }
  return out;
}

/** Default the v3-introduced optional fields on a single item. Pure;
 *  returns a new object if any defaults were added, otherwise the input. */
function defaultV3Fields(item: LayoutItem): LayoutItem {
  // Every kind that sends MIDI gets `destinations` defaulted to [1].
  // Buttons additionally pick up `mode='toggle'`, `shape='rect'`,
  // `actionType='cc'` to preserve their v2 behavior verbatim.
  switch (item.kind) {
    case 'button': {
      const out: ButtonItem = {
        ...item,
        mode: item.mode ?? 'toggle',
        shape: item.shape ?? 'rect',
        rotation: item.rotation ?? 0,
        actionType: item.actionType ?? 'cc',
        destinations: item.destinations ?? [...DEFAULT_DESTINATIONS],
      };
      return out;
    }
    case 'lfo':
    case 'buttongrid':
    case 'fader':
    case 'radial':
      return { ...item, destinations: item.destinations ?? [...DEFAULT_DESTINATIONS] };
    case 'statusLed': {
      // Defensive defaults for status LEDs authored on older client versions
      // (statusLed shipped in the same 3.x cycle as ButtonShape v2 but the
      // defaulter must survive round-trips through pre-shape client bundles).
      const out: StatusLedItem = {
        ...item,
        threshold: typeof item.threshold === 'number' ? item.threshold : 1,
        shape: item.shape ?? 'circle',
      };
      return out;
    }
    case 'display': {
      // Defensive defaults for a hand-edited/corrupted project that
      // round-trips without `source` set - 'bpm' needs zero extra config
      // to render something meaningful, same reasoning as addDisplay()'s
      // factory default.
      const out: DisplayItem = {
        ...item,
        source: item.source ?? 'bpm',
      };
      return out;
    }
    case 'presetChange': {
      // `device` is defaulted defensively: a project authored on a build
      // that predates a second device profile must still resolve to
      // something dispatchable. `channel`/`port` were removed 2026-08-04
      // (DEC-152 amended) — an item persisted before that has neither
      // `pedalId` nor a matching pedal to guess, so it resolves to the
      // same "no device assigned" dead state a never-authored tile gets,
      // rather than guessing wrong. Re-authoring via the palette is the
      // fix, same as it always was for an unconfigured tile.
      const out: PresetChangeItem = {
        ...item,
        device: item.device ?? 'helix',
        setlist: typeof item.setlist === 'number' ? item.setlist : 0,
        program: typeof item.program === 'number' ? item.program : 0,
        destinations: item.destinations ?? [...DEFAULT_DESTINATIONS],
      };
      return out;
    }
    case 'snapshotChange': {
      // Same defensive shape as presetChange, including the 2026-08-04
      // channel/port removal note above. `snapshot` additionally guards
      // against a persisted value that is neither a number nor one of the two
      // relative moves — anything unrecognised falls back to snapshot 1
      // rather than dispatching a NaN CC value the device would ignore.
      const snap = item.snapshot;
      const out: SnapshotChangeItem = {
        ...item,
        device: item.device ?? 'helix',
        snapshot: (typeof snap === 'number' || snap === 'next' || snap === 'prev') ? snap : 0,
        destinations: item.destinations ?? [...DEFAULT_DESTINATIONS],
      };
      return out;
    }
    case 'wavesAutotune': {
      // channel + scalePlacement were added after the item's first ship;
      // default them so older persisted items render + dispatch correctly.
      const out: WavesAutotuneItem = {
        ...item,
        channel: typeof item.channel === 'number' ? item.channel : 2,
        scalePlacement: item.scalePlacement ?? 'bottom',
      };
      return out;
    }
    // sequencer / macro / streamdeck / image don't get the destinations
    // field (sequencer + macro route per-lane; streamdeck talks SD not
    // MIDI; image doesn't send anything).
    default:
      return item;
  }
}

// ─── v3 → v4 migration ───────────────────────────────────────────
// v4 exists for exactly one reason: the `iphone-12-pro-max` preset was
// stated in PHYSICAL pixels (1284x2778) instead of CSS points (428x926),
// so every iPhone project's canvas was 3x too large and its items were
// authored in that oversized space. Correcting the preset alone would
// leave those items sitting off the bottom-right of a canvas a third of
// the size, which reads as data loss even though nothing was lost.
//
// So: rescale the geometry of every item in an iPhone project by exactly
// 1/3, once, at the same moment the preset changes. iPad projects are
// untouched - their preset was always right, and rescaling them would
// wreck layouts the operator gigs with.
//
// Rounded to integers because item geometry is whole pixels everywhere
// else (snapping, squareFloor, the 35x35 minimum). 1284/3 is exact, so
// the rounding only bites on coordinates that were already fractional.

export function migrateV3toV4(v3: ProjectsState): ProjectsState {
  const out: ProjectsState = { projects: {}, order: [...v3.order] };
  for (const [id, p] of Object.entries(v3.projects)) {
    if (p.device !== 'iphone-12-pro-max') {
      out.projects[id] = p;
      continue;
    }
    const k = LEGACY_IPHONE_FIX_FACTOR;
    out.projects[id] = {
      ...p,
      pages: p.pages.map((page) => ({
        ...page,
        items: page.items.map((it) => ({
          ...it,
          x: Math.round(it.x * k),
          y: Math.round(it.y * k),
          w: Math.max(1, Math.round(it.w * k)),
          h: Math.max(1, Math.round(it.h * k)),
        })),
      })),
    };
  }
  return out;
}

/**
 * v4 -> v5: re-widen SceneCopy items for the CLEAR column.
 *
 * Device v0.4 added `clear_scene`, so the item went from two columns
 * (COPY | PASTE) to three (COPY | PASTE | CLEAR). Items persisted before
 * that carry `w = 2 * cellW` and would render three columns squeezed into a
 * two-column box - not broken, but wrong enough to mis-tap, and mis-tapping
 * this item empties clip slots.
 *
 * Recomputes w and h from `count` + `size`, which is IDEMPOTENT (it derives
 * from the item's own fields rather than scaling what is already there).
 * That matters: `loadProjects` re-runs the defensive pass on every load, and
 * migrateV3toV4 had to be excluded from that exact path because scaling by a
 * factor shrinks an already-corrected project on every load. This one can be
 * run any number of times safely.
 *
 * `count` / `firstScene` are defaulted defensively - an item written by a
 * mid-cycle dev build could predate either field.
 *
 * Also (2026-10-15): an OP-Quantize Rec Quantize row saved while its Amount
 * was a BAND under the pads is one cell short and 1.45 cells tall; the
 * Amount is now the row's last cell. Recognised by EXACTLY that saved
 * geometry (w = labels x cell, h = round(cell x 1.45) at its size), so a row
 * the operator resized by hand is left alone and the pass stays idempotent:
 * once fixed, the width no longer matches the old shape.
 */
export function migrateV4toV5(v4: ProjectsState): ProjectsState {
  const out: ProjectsState = { projects: {}, order: [...v4.order] };
  for (const [id, p] of Object.entries(v4.projects)) {
    out.projects[id] = {
      ...p,
      pages: p.pages.map((page) => ({
        ...page,
        items: page.items.map((it) => {
          if (it.kind === 'recQuant') return resizeBandedOpQuantizeRow(it as RecQuantItem);
          if (it.kind !== 'sceneCopy') return it;
          const sc = it as SceneCopyItem;
          const count = Math.max(1, Math.min(8, Math.round(sc.count ?? 5)));
          const cell = sizeFor('sceneCopy', sc.size ?? 'M');
          const fields = { ...sc, firstScene: Math.max(1, Math.round(sc.firstScene ?? 1)), count };
          // Only the OLD shape (two columns, `w = 2 * cellW`) is re-widened. This runs
          // on EVERY load, and recomputing every Scene Copy squeezed the DELETE ALL
          // bar into the slot rows (the shape its own setter warns empties clip slots
          // on a mis-tap) and undid any hand resize, then lost the tie to the synced
          // copy: a "Synced from another device" toast and an undo entry on every
          // boot (2026-11-03 audit E-8). The bar's row is `SCENE_COPY_DELETE_ALL_ROW`
          // (0.8 of a cell, layout/store.ts `sceneCopyDims`), repeated here because
          // store.ts imports this module.
          if (sc.w !== 2 * cell.w) {
            return sc.count === count && sc.firstScene === fields.firstScene ? sc : (fields satisfies SceneCopyItem);
          }
          return {
            ...fields,
            w: SCENE_COPY_COLUMNS * cell.w,
            h: count * cell.h + (sc.showDeleteAll ? Math.round(cell.h * 0.8) : 0),
          } satisfies SceneCopyItem;
        }),
      })),
    };
  }
  return out;
}

/** See `migrateV4toV5`: one OP-Quantize row from the Amount-band shape to
 *  the Amount-cell shape, and nothing else. */
export function resizeBandedOpQuantizeRow(rq: RecQuantItem): RecQuantItem {
  if (rq.quantizer !== 'op-quantize' || !Array.isArray(rq.labels)) return rq;
  const cell = sizeFor('recQuant', rq.size ?? 'M');
  const n = Math.max(1, rq.labels.length);
  if (rq.w !== n * cell.w || rq.h !== Math.round(cell.h * 1.45)) return rq;
  return { ...rq, w: (n + 1) * cell.w, h: cell.h };
}

function loadProjects(): ProjectsState {
  if (typeof localStorage === 'undefined') return { projects: {}, order: [] };
  // Try v5 first (current).
  const rawV5 = localStorage.getItem(PROJECTS_KEY_V5);
  try {
    if (rawV5) {
      const parsed = JSON.parse(rawV5) as ProjectsState;
      if (parsed.projects && Array.isArray(parsed.order)) {
        // Defensive: re-run item defaults in case the on-disk shape
        // predates a field we've since added (mid-cycle dev iteration).
        // NOT migrateV3toV4 - that one is not idempotent and would shrink
        // an already-corrected iPhone project by another third on every
        // single load. migrateV4toV5 IS idempotent (it recomputes geometry
        // from the item's own count/size), so it is safe on this path.
        return migrateV4toV5(migrateV2toV3(parsed));
      }
    }
  } catch (err) {
    // 2026-10-18: the operator's iPad came up on the first-run welcome. A
    // stored set that throws here falls through to older keys - or, with
    // none, to EMPTY, and the next save() would write that empty set over
    // the only copy. Keep the unreadable text under its own key first, so a
    // load bug can never become data loss.
    console.error('[projects] stored projects could not be loaded; kept a copy', err);
    try { localStorage.setItem(`${PROJECTS_KEY_V5}.unreadable-${Date.now()}`, rawV5 ?? ''); } catch { /* quota: nothing more we can do */ }
  }
  // Fall back to v4 -> migrate (this is where the SceneCopy CLEAR column
  // re-widen lands).
  try {
    const raw = localStorage.getItem(PROJECTS_KEY_V4);
    if (raw) {
      const parsed = JSON.parse(raw) as ProjectsState;
      if (parsed.projects && Array.isArray(parsed.order)) {
        const migrated = migrateV4toV5(migrateV2toV3(parsed));
        localStorage.setItem(PROJECTS_KEY_V5, JSON.stringify(migrated));
        return migrated;
      }
    }
  } catch { /* fall through to v3 */ }
  // Fall back to v3 → migrate (this is where the iPhone preset fix lands).
  try {
    const raw = localStorage.getItem(PROJECTS_KEY_V3);
    if (raw) {
      const parsed = JSON.parse(raw) as ProjectsState;
      if (parsed.projects && Array.isArray(parsed.order)) {
        const v4 = migrateV3toV4(migrateV2toV3(parsed));
        const v5 = migrateV4toV5(v4);
        localStorage.setItem(PROJECTS_KEY_V4, JSON.stringify(v4));
        localStorage.setItem(PROJECTS_KEY_V5, JSON.stringify(v5));
        return v5;
      }
    }
  } catch { /* fall through to v2 */ }
  // Fall back to v2 → migrate.
  try {
    const raw = localStorage.getItem(PROJECTS_KEY_V2);
    if (raw) {
      const parsed = JSON.parse(raw) as ProjectsState;
      if (parsed.projects && Array.isArray(parsed.order)) {
        const v3 = migrateV2toV3(parsed);
        const v4 = migrateV3toV4(v3);
        const v5 = migrateV4toV5(v4);
        localStorage.setItem(PROJECTS_KEY_V3, JSON.stringify(v3));
        localStorage.setItem(PROJECTS_KEY_V4, JSON.stringify(v4));
        localStorage.setItem(PROJECTS_KEY_V5, JSON.stringify(v5));
        return v5;
      }
    }
  } catch { /* fall through to v1 */ }
  // Fall back to v1 → migrate v1 → v2 → v3 → v4.
  try {
    const raw = localStorage.getItem(PROJECTS_KEY_V1);
    if (!raw) return { projects: {}, order: [] };
    const parsed = JSON.parse(raw) as ProjectsStateV1;
    if (!parsed.projects || !Array.isArray(parsed.order)) return { projects: {}, order: [] };
    const v2 = migrateV1toV2(parsed);
    const v3 = migrateV2toV3(v2);
    const v4 = migrateV3toV4(v3);
    const v5 = migrateV4toV5(v4);
    // Write to v2 + v3 + v4 + v5 so subsequent loads skip the chain. Leave
    // v1 untouched as a safety net.
    localStorage.setItem(PROJECTS_KEY_V2, JSON.stringify(v2));
    localStorage.setItem(PROJECTS_KEY_V3, JSON.stringify(v3));
    localStorage.setItem(PROJECTS_KEY_V4, JSON.stringify(v4));
    localStorage.setItem(PROJECTS_KEY_V5, JSON.stringify(v5));
    return v5;
  } catch {
    return { projects: {}, order: [] };
  }
}

function loadActiveId(): string | null {
  if (typeof localStorage === 'undefined') return null;
  return localStorage.getItem(ACTIVE_KEY);
}

/** When the last save to this device FAILED (null = saving fine).
 *
 *  2026-10-18: the operator's iPad said "storage is full", then came up
 *  empty. On a full device every setItem throws, and save() let it escape:
 *  edits silently stopped being kept here, and the throw broke out of
 *  whatever gesture made the edit. Now the edit stands in memory (and still
 *  reaches OPBridge through projectSync, which reads the signal), and the
 *  operator is TOLD, once per episode, plus a pre-flight row. */
const [saveFailedAt, setSaveFailedAt] = createSignal<number | null>(null);
export { saveFailedAt as projectsSaveFailedAt };

function save(state: ProjectsState): void {
  if (typeof localStorage === 'undefined') return;
  try {
    localStorage.setItem(PROJECTS_KEY_V5, JSON.stringify(state));
    if (saveFailedAt() !== null) {
      setSaveFailedAt(null);
      pushToast('ok', 'Saving on this device again.');
    }
  } catch (err) {
    console.error('[projects] save failed', err);
    if (saveFailedAt() === null) {
      pushToast('error', "This device's storage is full, so changes are not being saved on it. Free up space now: a full iPad can wipe the app. OPBridge keeps a copy while you are connected.", 0);
    }
    setSaveFailedAt(Date.now());
  }
}

function saveActive(id: string | null): void {
  if (typeof localStorage === 'undefined') return;
  if (id === null) localStorage.removeItem(ACTIVE_KEY);
  else localStorage.setItem(ACTIVE_KEY, id);
}

const [stateSignal, setStateSignal] = createSignal<ProjectsState>(loadProjects());
const [activeIdSignal, setActiveIdSignal] = createSignal<string | null>(loadActiveId());

export const projects: Accessor<Project[]> = createMemo(() =>
  stateSignal().order
    .map((id) => stateSignal().projects[id])
    .filter((p): p is Project => p !== undefined),
);

export const activeProjectId: Accessor<string | null> = activeIdSignal;

export const activeProject: Accessor<Project | null> = createMemo(() => {
  const id = activeIdSignal();
  if (!id) return null;
  return stateSignal().projects[id] ?? null;
});

/** Pages of the active project, or [] if none. */
export const pagesOfActive: Accessor<Page[]> = createMemo(() => activeProject()?.pages ?? []);

/** The currently-active page within the active project. Falls back to
 *  pages[0] if activePageId doesn't resolve (defensive — addPage/delete
 *  paths always keep activePageId valid, but external migrations could
 *  desync it). */
export const activePage: Accessor<Page | null> = createMemo(() => {
  const p = activeProject();
  if (!p) return null;
  const hit = p.pages.find((pg) => pg.id === p.activePageId);
  return hit ?? p.pages[0] ?? null;
});

/** Canvas pixel dimensions for the active project. Null if no active project.
 *  Locked to the (device, orientation) chosen at project creation — items
 *  are stored in device pixels so a layout built for iPad-landscape replays
 *  identically on the iPad regardless of which screen it's loaded on. On
 *  off-target screens (laptop preview, wrong-orientation phone), the canvas
 *  scales-to-fit with letterboxing — that's correct: the canvas is honest
 *  about which screen it was designed for. */
export const activeCanvasSize: Accessor<{ w: number; h: number } | null> = createMemo(() => {
  const p = activeProject();
  if (!p) return null;
  return canvasSizeOf(p);
});

/** Stamp `modifiedAt` on every project whose object identity changed.
 *
 *  Every mutator in this file builds its next state immutably, so a
 *  changed reference IS a changed project — which means stamping can be
 *  done once, here, instead of in each of the ~18 exported mutators.
 *  That matters beyond tidiness: a mutator added later gets correct
 *  sync-conflict behaviour for free, whereas a per-call-site stamp is
 *  one omission away from a project that silently never wins a merge. */
function stampModified(prev: ProjectsState, next: ProjectsState, now: number): ProjectsState {
  let touched = false;
  const projects: Record<string, Project> = { ...next.projects };
  for (const id of Object.keys(projects)) {
    if (prev.projects[id] === projects[id]) continue;
    projects[id] = { ...projects[id]!, modifiedAt: now };
    touched = true;
  }
  return touched ? { ...next, projects } : next;
}

/** @param stamp - false only for state arriving FROM a sync merge, whose
 *  `modifiedAt` values are authoritative and must not be overwritten
 *  with local wall-clock time (that would make every pulled project look
 *  freshly edited here and win every subsequent merge). */
/** Set around a write that read-only still allows (a page switch: the
 *  performer must always be able to change page). */
let readOnlyAllowed = false;
let lastReadOnlyToastAt = 0;

function update(mutator: (s: ProjectsState) => ProjectsState, stamp = true): void {
  // Read-only (layout/docPresence.ts): the project is open elsewhere and this
  // window chose not to edit it, so no LOCAL write lands - not even a fader's
  // remembered value, whose stamp would win the merge over the other copy's
  // real edits. Merges arrive by another path and still apply.
  if (stamp && projectReadOnly() && !readOnlyAllowed) {
    const now = Date.now();
    if (now - lastReadOnlyToastAt > 3000) {
      lastReadOnlyToastAt = now;
      pushToast('warn', 'Read-only: this project is open elsewhere. "Edit anyway" is in the bar.', 4000);
    }
    return;
  }
  // Checkpoint pre-state BEFORE mutation applies, so undo restores what
  // the user was looking at just before this edit. Batches (drag flows,
  // multi-step palette insertions) collapse into one undo entry via the
  // history helper's lazy-capture semantics — call `beginBatch()` from
  // the drag-start / macro-open code path, `endBatch()` on release.
  projectHistory.checkpoint();
  const prev = stateSignal();
  const raw = mutator(prev);
  const next = stamp ? stampModified(prev, raw, Date.now()) : raw;
  setStateSignal(next);
  save(next);
}

/** A write that is NOT an authored edit: a page switch, a control's released value (2026-11-03 audit E-4 / F-8).
 *  Saved here, but never stamped (it must not win the whole-project last-writer-wins over another device's real
 *  edit), never an undo step (Ctrl+Z stepped back through fader moves, and 100 releases pushed every real edit out of
 *  history), and allowed while read-only (a performer's page and knob positions are theirs). */
function updateQuiet(mutator: (s: ProjectsState) => ProjectsState): void {
  const next = mutator(stateSignal());
  if (next === stateSignal()) return;
  setStateSignal(next);
  save(next);
}

/** Set one item's live value on the active page (a fader, knob, XY or light level on release). See `updateQuiet`. */
export function setLiveItemValue(itemId: string, patch: Partial<LayoutItem>): void {
  const id = activeIdSignal();
  if (!id) return;
  updateQuiet((s) => {
    const p = s.projects[id];
    if (!p) return s;
    return {
      ...s,
      projects: {
        ...s.projects,
        [id]: { ...p, pages: p.pages.map((pg) => (pg.id !== p.activePageId ? pg : { ...pg, items: pg.items.map((it) => (it.id === itemId ? ({ ...it, ...patch } as LayoutItem) : it)) })) },
      },
    };
  });
}

/** History handle for the projects doc — layout mutations, item edits,
 *  page add/delete/reorder, project rename/create, everything that goes
 *  through `update()` above. Undo/redo restores the whole projects state
 *  object; the state signal fires, everything re-renders. */
export const projectHistory = createHistory<ProjectsState>({
  name: 'projects',
  read: () => stateSignal(),
  write: (next) => {
    // Undo/redo is a real edit as far as other devices are concerned, so
    // it stamps too. Without this, undoing a change would restore the
    // project's OLD `modifiedAt` and the next sync would treat the
    // remote's un-undone copy as newer and put the change straight back.
    const stamped = stampModified(stateSignal(), next, Date.now());
    setStateSignal(stamped);
    save(stamped);
  },
});

export interface CreateProjectArgs {
  name: string;
  device: DeviceId;
  orientation: Orientation;
}

export function createProject(args: CreateProjectArgs): Project {
  const id = `prj-${Date.now().toString(36)}-${idSuffix()}`;
  const pageId = newPageId();
  const project: Project = {
    id,
    name: args.name.trim() || 'Untitled',
    device: args.device,
    orientation: args.orientation,
    pages: [{
      id: pageId,
      name: 'Page 1',
      items: [],
      color: 'sky',
    }],
    activePageId: pageId,
    nextCC: 60,
    createdAt: Date.now(),
  };
  update((s) => ({
    projects: { ...s.projects, [id]: project },
    order: [...s.order, id],
  }));
  switchProject(id);
  return project;
}

/** Insert a fully-formed project into the store. Used by the import
 *  flow when restoring a project from an exported bundle. The project
 *  is run through the v3 default-filler so any optional fields added
 *  since the bundle was exported are sensibly defaulted. Returns the
 *  id (always the project's own id; callers wanting "import as copy"
 *  should set a fresh id before calling). */
export function insertProject(project: Project, makeActive: boolean): string {
  const defaulted: Project = {
    ...project,
    pages: project.pages.map((page) => ({
      ...page,
      items: page.items.map(defaultV3Fields),
    })),
  };
  update((s) => ({
    projects: { ...s.projects, [defaulted.id]: defaulted },
    order: s.order.includes(defaulted.id) ? s.order : [...s.order, defaulted.id],
  }));
  if (makeActive) switchProject(defaulted.id);
  return defaulted.id;
}

/** Put back a project from a backup, AS IT WAS: same id, same name, and its
 *  own `modifiedAt` (plan 3.2, 2026-10-01). `insertProject` stamps the
 *  project as edited now, which for a restore is wrong in a way that loses
 *  work: the restored copy would beat the newer copy OPBridge holds at the next
 *  sync. The caller checks the id is free first. */
export function restoreProject(project: Project): string {
  const defaulted: Project = {
    ...project,
    pages: project.pages.map((page) => ({
      ...page,
      items: page.items.map(defaultV3Fields),
    })),
  };
  update((s) => ({
    projects: { ...s.projects, [defaulted.id]: defaulted },
    order: s.order.includes(defaulted.id) ? s.order : [...s.order, defaulted.id],
  }), false);
  return defaulted.id;
}

/** Apply the result of a cross-device sync merge (projectSync.ts).
 *
 *  `incoming` is ALREADY the merge of local and remote - the union of
 *  both devices' projects with per-project conflicts resolved and
 *  tombstoned deletions applied (`projectMerge.ts`). So this is a
 *  wholesale set of the project map, but it is NOT the old
 *  "replaceAllProjects" behaviour it replaced: nothing local is
 *  discarded on the way in, because anything local that should survive
 *  is already present in `incoming`.
 *
 *  Two things it deliberately does differently from a normal mutation:
 *   - `stamp = false`, so each project keeps the `modifiedAt` the merge
 *     chose. Stamping here would make every pulled project look edited
 *     on this device and win every future merge against its own source.
 *   - the active project is preserved when it survived the merge, so a
 *     sync landing mid-session does not yank the canvas out from under
 *     whoever is looking at it. */
export function applyMergedProjects(incoming: Project[]): void {
  // Keep every item whose content did not change: a fresh object for each
  // made <For each={layoutItems()}> remount EVERY control on the page on
  // every merge, mid-gesture included - a held button, a fader being ridden,
  // a drag (2026-11-03 audit F-5).
  const prev = stateSignal();
  const before = new Map<string, LayoutItem>();
  for (const p of Object.values(prev.projects)) {
    for (const pg of p.pages) for (const it of pg.items) before.set(`${p.id}::${it.id}`, it);
  }
  const keep = (projectId: string, it: LayoutItem): LayoutItem => {
    const d = defaultV3Fields(it);
    const old = before.get(`${projectId}::${it.id}`);
    // Compare DEFAULTED forms: `defaultV3Fields` always returns a fresh
    // object, and a stored item can lack a field it fills in.
    return old && canonicalJson(defaultV3Fields(old)) === canonicalJson(d) ? old : d;
  };
  const defaulted = incoming.map((project) => {
    // This device's page stays this device's: the merge used to move every device to whichever page the last
    // writer was on (2026-11-03 audit E-4). Kept only while that page still exists in the incoming copy.
    const mine = prev.projects[project.id]?.activePageId;
    const activePageId = mine && project.pages.some((pg) => pg.id === mine) ? mine : project.activePageId;
    return {
      ...project,
      activePageId,
      pages: project.pages.map((page) => ({
        ...page,
        items: page.items.map((it) => keep(project.id, it)),
      })),
    };
  });
  // OUTSIDE undo history, and the history is cleared, the way the timeline
  // store treats an arriving timeline. Checkpointed like a local edit, a merge
  // was the next thing Ctrl+Z undid: it restored the pre-merge library,
  // stamped it now, and that copy then WON the next sync, reverting the other
  // device's work on every device (2026-11-03 audit E-5). An undo stack of
  // whole-library snapshots cannot be replayed over someone else's change, so
  // losing local undo across a remote change is the smaller cost.
  const next: ProjectsState = {
    projects: Object.fromEntries(defaulted.map((p) => [p.id, p])),
    order: defaulted.map((p) => p.id),
  };
  setStateSignal(next);
  save(next);
  projectHistory.clear();
  const currentActive = activeIdSignal();
  const stillPresent = currentActive !== null && defaulted.some((p) => p.id === currentActive);
  if (!stillPresent) switchProject(defaulted[0]?.id ?? null);
}

export function deleteProject(id: string): void {
  // Record the deletion BEFORE mutating, so a tombstone exists even if
  // something below throws. Without this the next sync from any other
  // device would treat the project as merely "absent here" and push it
  // straight back — deletions would never stick. See projectMerge.ts.
  recordDeletion(id);
  // Its controls' matrix claims go with it (DEC-292).
  const doomedItems = (stateSignal().projects[id]?.pages ?? []).flatMap((pg) => pg.items.map((it) => it.id));
  update((s) => {
    const { [id]: _gone, ...rest } = s.projects;
    void _gone;
    return {
      projects: rest,
      order: s.order.filter((x) => x !== id),
    };
  });
  dropClaimsOfItems(doomedItems);
  // If we deleted the active project, fall back to the first remaining one
  // (or null if none left — the browser will be forced open).
  if (activeIdSignal() === id) {
    const remaining = stateSignal().order;
    switchProject(remaining[0] ?? null);
  }
}

export function switchProject(id: string | null): void {
  // Navigate's Recent list (per device). The project being LEFT is stamped
  // too, a millisecond earlier, so the one the app booted into is recorded
  // the first time anything else is opened.
  const leaving = activeIdSignal();
  const now = Date.now();
  if (leaving && leaving !== id) noteProjectOpened(leaving, now - 1);
  noteProjectOpened(id, now);
  setActiveIdSignal(id);
  saveActive(id);
}

export function renameProject(id: string, name: string): void {
  update((s) => {
    const p = s.projects[id];
    if (!p) return s;
    return { ...s, projects: { ...s.projects, [id]: { ...p, name: name.trim() || 'Untitled' } } };
  });
}

/** A copy of a project, as "<name> copy" (2026-10-05: the help articles found
 *  no way to duplicate a project short of export + import). New project and
 *  page ids, so the copy syncs as its own document; items keep theirs, since
 *  they are scoped to their project. Timelines stay owned by the original,
 *  and any the copy's launchers point at travel with it (timelinesForProject),
 *  so the copy plays them. Returns the new id, or null for an unknown id. */
/** A COPY of a project gets new ids for its controls, not only its pages
 *  (audit 2026-11-03 E-7). Several stores key a control by its id across
 *  EVERY project: the shared actions a pedal fires (`modShare/sync.ts` collects
 *  all projects' items into one map by id), the Matrix claims a control owns,
 *  controller bindings, toggles and LFOs on OPBridge. A duplicate that kept the
 *  ids shared all of them with its original, so editing the copy re-pointed the
 *  original's pedal. `map` is old id -> new id, for the copy's own references
 *  (`remapTimelineItemIds`). */
export function reidProjectItems(pages: readonly Page[]): { pages: Page[]; map: Map<string, string> } {
  const map = new Map<string, string>();
  const stamp = Date.now().toString(36);
  const out = pages.map((pg) => ({
    ...pg,
    id: newPageId(),
    items: pg.items.map((it) => {
      const id = `btn-${stamp}-${idSuffix()}`;
      map.set(it.id, id);
      return { ...it, id };
    }),
  }));
  return { pages: out, map };
}

/** A timeline moved onto a project copy, its control references pointed at the
 *  copy's new ids (`reidProjectItems`). Steps and lanes naming a control the
 *  map does not know are left as they were. */
export function remapTimelineItemIds<T extends { lanes?: readonly unknown[] }>(t: T, map: ReadonlyMap<string, string>): T {
  if (!Array.isArray(t.lanes) || map.size === 0) return t;
  const lanes = (t.lanes as Array<Record<string, unknown>>).map((lane) => {
    let next = lane;
    const ctl = lane['control'] as { itemId?: string } | undefined;
    if (ctl && typeof ctl.itemId === 'string' && map.has(ctl.itemId)) next = { ...next, control: { ...ctl, itemId: map.get(ctl.itemId)! } };
    const steps = lane['steps'];
    if (Array.isArray(steps) && steps.some((s) => s && s.kind === 'buttonRef' && map.has(s.buttonId))) {
      next = { ...next, steps: steps.map((s) => (s && s.kind === 'buttonRef' && map.has(s.buttonId) ? { ...s, buttonId: map.get(s.buttonId)! } : s)) };
    }
    return next;
  });
  return { ...t, lanes };
}

export function duplicateProject(id: string): string | null {
  const p = stateSignal().projects[id];
  if (!p) return null;
  const pages = reidProjectItems(p.pages).pages;
  const activeIndex = p.pages.findIndex((pg) => pg.id === p.activePageId);
  const now = Date.now();
  const copy: Project = {
    ...p,
    id: `prj-${now.toString(36)}-${idSuffix()}`,
    name: `${p.name} copy`,
    createdAt: now,
    pages,
    activePageId: pages[Math.max(0, activeIndex)]?.id ?? '',
  };
  return insertProject(copy, true);
}

export function updateProjectDevice(id: string, device: DeviceId, orientation: Orientation): void {
  update((s) => {
    const p = s.projects[id];
    if (!p) return s;
    // Choosing a PRESET deliberately drops any exact frame - otherwise the
    // picker would silently do nothing, which is worse than either outcome.
    const next = { ...p, device, orientation };
    delete (next as { canvas?: unknown }).canvas;
    // The measured strips belong to the measured frame: a different device
    // or orientation has its own, and the table answers until it is played.
    delete (next as { reserve?: unknown }).reserve;
    return { ...s, projects: { ...s.projects, [id]: next } };
  });
}

/** Pin this project's canvas to an exact size in CSS points, overriding the
 *  preset. `setProjectDevice` clears it again. */
export function setProjectCanvas(id: string, w: number, h: number): void {
  if (!(w > 0 && h > 0)) return;
  update((s) => {
    const p = s.projects[id];
    if (!p) return s;
    if (p.canvas && p.canvas.w === w && p.canvas.h === h) return s;
    return { ...s, projects: { ...s.projects, [id]: { ...p, canvas: { w, h } } } };
  });
}

/** Record the strips the playing device measured (see `Project.reserve`). */
export function setProjectReserve(id: string, top: number, bottom: number): void {
  if (!(top >= 0 && bottom >= 0)) return;
  update((s) => {
    const p = s.projects[id];
    if (!p) return s;
    if (p.reserve && p.reserve.top === top && p.reserve.bottom === bottom) return s;
    return { ...s, projects: { ...s.projects, [id]: { ...p, reserve: { top, bottom } } } };
  });
}

// ─── Page mutators ───────────────────────────────────────────────
// All mutators operate on the active project. They no-op silently if
// no project is active (the UI shouldn't allow add/switch/delete in
// that state, but defensive guards keep callers simple).

function mutateActiveProject(mutator: (p: Project) => Project): void {
  const id = activeIdSignal();
  if (!id) return;
  update((s) => {
    const p = s.projects[id];
    if (!p) return s;
    return { ...s, projects: { ...s.projects, [id]: mutator(p) } };
  });
}

/** Add a blank page at the end of pages[], switch to it, persist. */
export function addPage(): Page | null {
  const id = activeIdSignal();
  if (!id) return null;
  const existing = stateSignal().projects[id];
  if (!existing) return null;
  const pageId = newPageId();
  const idx = existing.pages.length;
  const newPage: Page = {
    id: pageId,
    name: `Page ${idx + 1}`,
    items: [],
    color: PAGE_HUE_PALETTE[idx % PAGE_HUE_PALETTE.length] ?? 'sky',
  };
  mutateActiveProject((p) => ({
    ...p,
    pages: [...p.pages, newPage],
    activePageId: pageId,
  }));
  return newPage;
}

/** Switch the active page. No-op if pageId already active or not found.
 *  Sets `pageAnimDirection` for one 180ms-ish window before committing so
 *  the layout canvas can fade-shift out, swap, fade back in. Respects
 *  prefers-reduced-motion: if the user opted out, we skip the animation
 *  and switch immediately. */
export function switchPage(pageId: string): void {
  const id = activeIdSignal();
  if (!id) return;
  const proj = stateSignal().projects[id];
  if (!proj) return;
  if (proj.activePageId === pageId) return;
  const fromIx = proj.pages.findIndex((pg) => pg.id === proj.activePageId);
  const toIx = proj.pages.findIndex((pg) => pg.id === pageId);
  if (toIx === -1) return;

  // Animation gate: prefers-reduced-motion = OFF the fade-shift entirely.
  const reduce = typeof window !== 'undefined'
    && typeof window.matchMedia === 'function'
    && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  if (reduce) {
    updateQuiet((s) => { const p = s.projects[id]; return p ? { ...s, projects: { ...s.projects, [id]: { ...p, activePageId: pageId } } } : s; });
    return;
  }

  const direction: PageAnimDirection = toIx > fromIx ? 'left' : 'right';
  setPageAnimDirection(direction);
  // ~180ms — matches the CSS transition on .scaled-canvas-inner in app.css.
  // Splitting the swap into two phases lets the canvas fade out first,
  // re-render with new content, then fade back in.
  window.setTimeout(() => {
    updateQuiet((s) => { const p = s.projects[id]; return p ? { ...s, projects: { ...s.projects, [id]: { ...p, activePageId: pageId } } } : s; });
    // Tick: clear the anim flag on the next frame so the canvas's
    // CSS transition runs the "back in" half.
    requestAnimationFrame(() => setPageAnimDirection(null));
  }, 180);
}

/** Go `delta` pages along from the active one (+1 next, -1 previous), with the same animation as a tap on a page.
 *  Stops at the first and last page rather than wrapping. One implementation for every "next / previous page": the
 *  page pills' arrows and the two- or three-finger swipe on the canvas (2026-10-25). Returns whether it moved. */
export function stepPage(delta: number): boolean {
  const id = activeIdSignal();
  const proj = id ? stateSignal().projects[id] : undefined;
  if (!proj) return false;
  const ix = proj.pages.findIndex((pg) => pg.id === proj.activePageId);
  const target = ix === -1 ? undefined : proj.pages[ix + delta];
  if (!target) return false;
  switchPage(target.id);
  return true;
}

/** Page-switch animation flag. ScaledCanvas reads this and sets a
 *  `data-anim` attribute on .scaled-canvas-inner; CSS in app.css handles
 *  the fade-shift. Null = no animation in progress. Exposed for the canvas
 *  wrapper in App.tsx. */
export type PageAnimDirection = 'left' | 'right';
const [pageAnimDirectionSignal, setPageAnimDirection] = createSignal<PageAnimDirection | null>(null);
export const pageAnimDirection: Accessor<PageAnimDirection | null> = pageAnimDirectionSignal;

/** Delete a page. No-op if it's the only page left (UI disables the
 *  button). If deleting the active page, switches to the previous one
 *  in the list (or the next if deleting index 0). */
export function deletePage(pageId: string): void {
  const id = activeIdSignal();
  if (!id) return;
  const proj = stateSignal().projects[id];
  if (!proj) return;
  if (proj.pages.length <= 1) return;
  const idx = proj.pages.findIndex((pg) => pg.id === pageId);
  if (idx === -1) return;
  const nextPages = proj.pages.filter((pg) => pg.id !== pageId);
  let nextActive = proj.activePageId;
  if (proj.activePageId === pageId) {
    const fallbackIx = idx === 0 ? 0 : idx - 1;
    nextActive = nextPages[fallbackIx]?.id ?? nextPages[0]!.id;
  }
  mutateActiveProject((p) => ({ ...p, pages: nextPages, activePageId: nextActive }));
  // Its controls' matrix claims go with them (DEC-292).
  dropClaimsOfItems(proj.pages[idx]!.items.map((it) => it.id));
}

/** Duplicate a page — deep-clone, fresh ids for the page and every item
 *  inside (so per-item edits on the copy don't bleed into the source).
 *  Inserts immediately after the source and switches to it. */
export function duplicatePage(pageId: string): Page | null {
  const id = activeIdSignal();
  if (!id) return null;
  const proj = stateSignal().projects[id];
  if (!proj) return null;
  const idx = proj.pages.findIndex((pg) => pg.id === pageId);
  if (idx === -1) return null;
  const src = proj.pages[idx]!;
  const newId = newPageId();
  const clone: Page = {
    id: newId,
    name: `${src.name} (copy)`,
    color: src.color,
    items: src.items.map((it) => cloneItemWithNewIds(it)),
    ...(src.triggers ? { triggers: structuredClone(src.triggers) } : {}),
  };
  const nextPages = [
    ...proj.pages.slice(0, idx + 1),
    clone,
    ...proj.pages.slice(idx + 1),
  ];
  mutateActiveProject((p) => ({ ...p, pages: nextPages, activePageId: newId }));
  return clone;
}

/** Reorder a page. Splice fromIx out, splice into toIx. activePageId
 *  stays the same (still points at the same page id, just at its new
 *  index). No-op if indices are equal or out of bounds. */
export function reorderPages(fromIx: number, toIx: number): void {
  const id = activeIdSignal();
  if (!id) return;
  const proj = stateSignal().projects[id];
  if (!proj) return;
  if (fromIx === toIx) return;
  if (fromIx < 0 || fromIx >= proj.pages.length) return;
  if (toIx < 0 || toIx >= proj.pages.length) return;
  const next = [...proj.pages];
  const [moved] = next.splice(fromIx, 1);
  if (!moved) return;
  next.splice(toIx, 0, moved);
  mutateActiveProject((p) => ({ ...p, pages: next }));
}

/** Rename a page. Trims whitespace; falls back to "Page N" (current
 *  1-based index) if the trimmed string is empty. */
export function renamePage(pageId: string, name: string): void {
  const id = activeIdSignal();
  if (!id) return;
  const proj = stateSignal().projects[id];
  if (!proj) return;
  const idx = proj.pages.findIndex((pg) => pg.id === pageId);
  if (idx === -1) return;
  const trimmed = name.trim() || `Page ${idx + 1}`;
  mutateActiveProject((p) => ({
    ...p,
    pages: p.pages.map((pg) => (pg.id === pageId ? { ...pg, name: trimmed } : pg)),
  }));
}

/** Cycle a page's hue to the next one in the palette. Used by the page
 *  manager modal's color swatch button (no full picker in v1). */
export function cyclePageColor(pageId: string): void {
  const id = activeIdSignal();
  if (!id) return;
  const proj = stateSignal().projects[id];
  if (!proj) return;
  mutateActiveProject((p) => ({
    ...p,
    pages: p.pages.map((pg) => {
      if (pg.id !== pageId) return pg;
      const ix = PAGE_HUE_PALETTE.indexOf(pg.color);
      const nextHue = PAGE_HUE_PALETTE[(ix + 1) % PAGE_HUE_PALETTE.length] ?? 'sky';
      return { ...pg, color: nextHue };
    }),
  }));
}

/** Fresh-id deep clone for items duplicated alongside their page. Cells
 *  inside grids also get new ids so per-cell overrides survive but
 *  don't share identity with the source page's cells.
 *
 *  Exported 2026-08-09 for the item context menu's Duplicate / Copy / Paste
 *  — one clone rule for "a copy of this item", so a per-kind id quirk (the
 *  `grid-` prefix, the per-cell ids) can only ever be got right or wrong in
 *  ONE place.
 *
 *  What it deliberately does NOT do: renumber CC. Library-backed kinds
 *  (sequencer, macro, timeline launcher) keep pointing at the SAME library
 *  entry too: the copy is a copy of the control, not a fork of the pattern
 *  behind it.
 *
 *  AMENDED 2026-08-09. The no-renumber rule used to be argued here as
 *  universal - "a silently-different CC is the worse surprise, and the
 *  pre-flight lint catches the collision anyway". That reasoning survives
 *  only for PAGE-duplicate, its original and now only caller with that
 *  behaviour: mirroring a page onto the same CCs is usually the point of
 *  duplicating a page. It never held for a single item, where two faders
 *  driving one Ableton parameter is nobody's intent, and the operator said
 *  so. So the ITEM-level copy paths (`store.ts`'s `duplicateItem` /
 *  `pasteItem`) now renumber via `layout/midiAlloc.ts` AFTER calling this  - 
 *  renumbering stays out of here precisely so page-duplicate keeps the old
 *  behaviour rather than inheriting a change it never asked for. */
export function cloneItemWithNewIds(item: LayoutItem): LayoutItem {
  const baseId = `${item.kind === 'buttongrid' ? 'grid' : item.kind}-${Date.now().toString(36)}-${idSuffix()}`;
  if (item.kind === 'buttongrid') {
    return {
      ...structuredClone(item),
      id: baseId,
      cells: item.cells.map((c, i) => ({
        ...structuredClone(c),
        id: `cell-${i}-${idSuffix()}`,
      })),
    };
  }
  return { ...structuredClone(item), id: baseId };
}

// ─── Active-page item helpers ────────────────────────────────────
// These replace the old project-flat setActiveItems / setActiveNextCC
// helpers. Items live on the active page; nextCC stays at project root.
// The layout store's facade (`layoutItems()`, `nextCC()`, mutators)
// reads/writes through these.

function mutateActivePage(mutator: (page: Page) => Page): void {
  const id = activeIdSignal();
  if (!id) return;
  update((s) => {
    const p = s.projects[id];
    if (!p) return s;
    const pageId = p.activePageId;
    return {
      ...s,
      projects: {
        ...s.projects,
        [id]: {
          ...p,
          pages: p.pages.map((pg) => (pg.id === pageId ? mutator(pg) : pg)),
        },
      },
    };
  });
}

export function setActiveItems(items: LayoutItem[]): void {
  mutateActivePage((pg) => ({ ...pg, items }));
}

export function setActiveNextCC(nextCC: number): void {
  mutateActiveProject((p) => ({ ...p, nextCC }));
}

// ─── OP-Motion landing presets (2026-10-24) ────────────────────────

export function activeMotionPresets(): MotionPreset[] {
  return activeProject()?.motionPresets ?? [];
}

export function motionPresetById(id: string | undefined): MotionPreset | undefined {
  return id ? activeMotionPresets().find((p) => p.id === id) : undefined;
}

/** "Landing N", the first N no preset already uses. */
export function nextMotionPresetName(): string {
  const taken = new Set(activeMotionPresets().map((p) => p.name));
  let n = 1;
  while (taken.has(`Landing ${n}`)) n++;
  return `Landing ${n}`;
}

/** Add a preset, or replace the one with its id. */
export function saveMotionPreset(preset: MotionPreset): void {
  mutateActiveProject((p) => {
    const list = p.motionPresets ?? [];
    const at = list.findIndex((x) => x.id === preset.id);
    return { ...p, motionPresets: at < 0 ? [...list, preset] : list.map((x, i) => (i === at ? preset : x)) };
  });
}

export function renameMotionPreset(id: string, name: string): void {
  const n = name.trim();
  if (!n) return;
  mutateActiveProject((p) => ({ ...p, motionPresets: (p.motionPresets ?? []).map((x) => (x.id === id ? { ...x, name: n } : x)) }));
}

/** Remove a preset. A button still naming it then reads "No preset", and lands nothing. */
export function removeMotionPreset(id: string): void {
  mutateActiveProject((p) => ({ ...p, motionPresets: (p.motionPresets ?? []).filter((x) => x.id !== id) }));
}

/** Whether any control on the project captures or lands a preset: the reply rule is pushed only then. */
export function projectUsesMotionPresets(): boolean {
  const proj = activeProject();
  if (!proj) return false;
  return proj.pages.some((pg) => pg.items.some((i) => 'abletonSteps' in i
    && (i.abletonSteps ?? []).some((s) => s.type === 'opmotion.capture' || s.type === 'opmotion.land')));
}

// ─── Guitar FX pedal registry (Settings > Guitar FX Pedals) ────────

export function activePedals(): GuitarPedal[] {
  return activeProject()?.pedals ?? [];
}

export function setActivePedals(pedals: GuitarPedal[]): void {
  mutateActiveProject((p) => ({ ...p, pedals }));
}

export function addPedal(pedal: Omit<GuitarPedal, 'id'>): GuitarPedal {
  const id = `pedal-${Date.now().toString(36)}-${idSuffix()}`;
  const full: GuitarPedal = { ...pedal, id };
  setActivePedals([...activePedals(), full]);
  return full;
}

export function updatePedal(id: string, patch: Partial<Omit<GuitarPedal, 'id'>>): void {
  setActivePedals(activePedals().map((pd) => (pd.id === id ? { ...pd, ...patch } : pd)));
}

export function removePedal(id: string): void {
  setActivePedals(activePedals().filter((pd) => pd.id !== id));
}

/** A pedal's (port, channel) collides with another's when both are set and
 *  equal. Two pedals sharing a channel number on DIFFERENT ports do not
 *  conflict — a channel is scoped to its own port, not global (the same
 *  confusion the operator flagged when this was designed). `excludeId` lets
 *  a Settings edit check "would this change collide with anyone ELSE"
 *  without the pedal being edited comparing against its own prior value. */
export function pedalConflict(port: string | undefined, channel: number, excludeId?: string): GuitarPedal | undefined {
  if (!port) return undefined;
  return activePedals().find((pd) => pd.id !== excludeId && pd.port === port && pd.channel === channel);
}

/** Every item in the active project with a `midiTrigger` binding, across
 *  all pages — not just the visible one (mirrors DEC-154 sub-decision 1's
 *  "look up itemId across all pages" contract for the fire side; the
 *  author side needs the same reach for conflict-checking and rule-sync). */
export function allMidiTriggerItems(): LayoutItem[] {
  const proj = activeProject();
  if (!proj) return [];
  return proj.pages.flatMap((pg) => pg.items).filter((i) => 'midiTrigger' in i && i.midiTrigger);
}

/** Sentinel `excludeId` for the Panic MIDI trigger (Settings-level, not a
 *  canvas item — has no real item id to exclude-self-conflict with). Item
 *  ids are ULID-generated (`ulid()`), so this literal string can never
 *  collide with a real one. */
export const PANIC_TRIGGER_ID = 'panic';

function bindingMatches(
  b: MidiTriggerBinding,
  port: string,
  match: { type: 'note' | 'cc'; number: number; channel: number },
): boolean {
  return b.port === port && b.match.type === match.type
    && b.match.number === match.number && b.match.channel === match.channel;
}

export interface MidiTriggerConflict { label: string }

/** OPBridge's rules engine fires EVERY enabled rule whose condition matches
 *  — no implicit first-match-wins (confirmed against `Engine::evaluate`,
 *  see the DEC-154 design notepad) — so two bindings sharing the same
 *  (port, type, number, channel) would both fire on one pedal press. Same
 *  shape of problem as {@link pedalConflict}; checked at Learn time. Checks
 *  both binding-owner shapes this project has: per-item bindings AND the
 *  single project-level Panic binding (`Project.panicMidiTrigger`) — the
 *  latter has no canvas item, so it's checked separately rather than living
 *  in `allMidiTriggerItems()`. */
export function midiTriggerConflict(
  port: string,
  match: { type: 'note' | 'cc'; number: number; channel: number },
  excludeId?: string,
): MidiTriggerConflict | undefined {
  const item = allMidiTriggerItems().find((i) => {
    if (i.id === excludeId) return false;
    const b = (i as { midiTrigger?: MidiTriggerBinding }).midiTrigger;
    return b ? bindingMatches(b, port, match) : false;
  });
  if (item) return { label: item.name?.trim() || item.id };

  const panicBinding = activeProject()?.panicMidiTrigger;
  if (panicBinding && excludeId !== PANIC_TRIGGER_ID && bindingMatches(panicBinding, port, match)) {
    return { label: 'Panic' };
  }
  return undefined;
}

/** DEC-154's one non-item binding — Settings-level, project-scoped (travels
 *  with the project like everything else rig-shaped, DEC-076's pattern).
 *  See `MidiTriggerBinding`'s doc comment for the shared contract; `path`
 *  is always `'a'` here — OPBridge fires the raw MIDI bytes natively, no
 *  PWA tab needed (see `midiTrigger/ruleSync.ts`'s panic section for the
 *  one real limitation: the engine-stop half of panic isn't reachable from
 *  a rule action yet, ask filed to OPBridge). */
export function setPanicMidiTrigger(binding: MidiTriggerBinding | undefined): void {
  mutateActiveProject((p) => ({ ...p, panicMidiTrigger: binding }));
}

/** Write the guitar drop tuning onto the active project (synced). Callers go
 *  through `transpose/session.ts::setDropTuning`, which clamps. */
export function setProjectDropTuning(v: number): void {
  mutateActiveProject((p) => (p.dropTuning === v ? p : { ...p, dropTuning: v }));
}

export function setActiveItemsAndNextCC(items: LayoutItem[], nextCC: number): void {
  const id = activeIdSignal();
  if (!id) return;
  update((s) => {
    const p = s.projects[id];
    if (!p) return s;
    const pageId = p.activePageId;
    return {
      ...s,
      projects: {
        ...s.projects,
        [id]: {
          ...p,
          nextCC,
          pages: p.pages.map((pg) => (pg.id === pageId ? { ...pg, items } : pg)),
        },
      },
    };
  });
}

// ─── Z-order (front-to-back) ────────────────────────────────────
// Items render in array order: index 0 paints first (back), the last
// index paints last (front, on top). The InspectorPanel surfaces the four
// canonical actions plus an "N of M" position indicator that reads
// directly off the array index.

/** Position of `itemId` within the active page's items[].
 *  Returns `{ index, total }` (1-indexed for UI display) or null if not
 *  found. */
export function itemStackPosition(itemId: string): { index: number; total: number } | null {
  const pg = activePage();
  if (!pg) return null;
  const i = pg.items.findIndex((it) => it.id === itemId);
  if (i < 0) return null;
  return { index: i + 1, total: pg.items.length };
}

function moveItemTo(itemId: string, targetIx: number): void {
  mutateActivePage((pg) => {
    const i = pg.items.findIndex((it) => it.id === itemId);
    if (i < 0) return pg;
    const next = pg.items.slice();
    const [moved] = next.splice(i, 1);
    if (!moved) return pg;
    const clamped = Math.max(0, Math.min(next.length, targetIx));
    next.splice(clamped, 0, moved);
    return { ...pg, items: next };
  });
}

/** Move one slot toward the front (higher index). */
export function bringItemForward(itemId: string): void {
  const pg = activePage();
  if (!pg) return;
  const i = pg.items.findIndex((it) => it.id === itemId);
  if (i < 0 || i >= pg.items.length - 1) return;
  moveItemTo(itemId, i + 1);
}

/** Move one slot toward the back (lower index). */
export function sendItemBackward(itemId: string): void {
  const pg = activePage();
  if (!pg) return;
  const i = pg.items.findIndex((it) => it.id === itemId);
  if (i <= 0) return;
  moveItemTo(itemId, i - 1);
}

/** Move to the very front (renders on top of everything). */
export function bringItemToFront(itemId: string): void {
  const pg = activePage();
  if (!pg) return;
  moveItemTo(itemId, pg.items.length);
}

/** Move to the very back (renders behind everything). */
export function sendItemToBack(itemId: string): void {
  moveItemTo(itemId, 0);
}
